import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { SchoolSwitcher, SchoolSelector } from '../components/SchoolSwitcher';
import { LogoutConfirmationModal } from '../components/LogoutConfirmationModal';
import {
  fetchAnnouncements, 
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement, 
  type AnnouncementDto, 
  type CreateAnnouncementPayload,
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  permanentlyDeleteUser,
  fetchUserById,
  fetchNextUserLoginId,
  type UserDto,
  fetchSubjects,
  createSubject,
  type SubjectDto,
  fetchTeacherSubjects,
  createTeacherSubject,
  updateTeacherSubject,
  deleteTeacherSubject,
  clearTeacherSubjectsForForm,
  bulkSaveTeacherSubjects,
  type TeacherSubjectDto,
  fetchAuditLogs,
  type AuditLogDto,
  type AuditLogFilters,
  fetchGradeAnalytics,
  type GradeAnalyticsDto,
  fetchTimetables,
  generateTimetable,
  publishTimetable,
  clearTimetable,
  updateTimetable,
  deleteTimetable,
  createTimetable,
  type TimetableDto,
  type TimetableConflict,
  getUserPasswordInfo,
  resetUserPassword,
  type UserPasswordInfo,
  fetchPasswordResetRequests,
  approvePasswordReset,
  rejectPasswordReset,
  type PasswordResetRequestDto,
  getGradeSubmissionStatus,
  fetchClasses,
  fetchActiveClasses,
  createClass,
  updateClass,
  deleteClass,
  type SchoolClassDto,
  type CreateSchoolClassPayload,
  fetchClubs,
  fetchClubMembers,
  addClubMember,
  removeClubMember,
  updateClubMemberRole,
  createClub,
  updateClub,
  deleteClub,
  type ClubDto,
  type ClubMemberDto,
  type CreateClubPayload,
  uploadAvatar,
  updateUserProfile,
  fetchSchoolSettings,
  updateSchoolSettings,
  advanceSchoolTerm,
  exportTermData,
  adminSetBoardingStatus,
  adminClearBoardingStatuses,
  fetchAdminBoardingStatuses,
  type AdminBoardingStatusEntryDto,
  type SchoolSettingsDto,
} from '../api/axios';
import { AdminGradeReviewSection } from '../components/grading';
import { buildTermExportPdf } from '../utils/termExportPdf';
import { ReportCardManagement } from '../components/ReportCardManagement';
import { TimetableGrid } from '../components/timetable';
import { FeeManagementGate } from '../components/FeeManagementGate';
import { StudentImportModal } from '../components/StudentImportModal';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  type ClassStudentDto,
  type ClassStudentsResponse,
  fetchClassStudentsByClassId,
  bulkProgressStudents,
} from '../api/axios';
import styles from './AdminDashboard.module.css';
import { formatGradeForContext } from '../utils/letterGrading';
import type { SchoolCode } from '../types';
import { SHARED_SCHOOL_MOTTO } from '../schoolBranding';
import { waitMinDuration } from '../utils/uiLoading';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { classLevelWord, formatClassLevelLabel } from '../utils/classLevelLabel';

// #region agent log
const debugTimetableLog = (
  message: string,
  data: Record<string, unknown>,
  hypothesisId: string,
  runId: string,
) => {
  const canSendLocalDebugIngest =
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');
  if (!canSendLocalDebugIngest) return;

  try {
    // eslint-disable-next-line no-void
    void fetch('http://127.0.0.1:7245/ingest/56c43c32-c6e3-4e81-9da5-fafbd046edde', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Debug-Session-Id': 'e0444e',
      },
      body: JSON.stringify({
        sessionId: 'e0444e',
        location: 'AdminDashboard.tsx',
        message,
        data,
        hypothesisId,
        runId,
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  } catch {
    // swallow
  }
};
// #endregion

type AdminSection = 
  | 'dashboard'
  | 'user-management'
  | 'password-management'
  | 'grade-review'
  | 'grades'
  | 'announcements'
  | 'timetable-management'
  | 'class-teacher-linking'
  | 'class-management'
  | 'club-management'
  | 'fee-structure'
  | 'fee-history'
  | 'fee-report'
  | 'report-cards'
  | 'audit-log'
  | 'term-configuration'
  | 'boarding-controls'
  | 'system-settings'
  | 'profile';

// ── Subsystem groupings ─────────────────────────────────────────────────────

type SubsystemId = 'academics' | 'people' | 'finance' | 'analytics';

interface NavGroup {
  id: SubsystemId;
  label: string;
  icon: string;
  color: string;
  description: string;
  items: { id: AdminSection; label: string; icon: string }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'academics',
    label: 'Academics',
    icon: '🎓',
    color: '#2563eb',
    description: 'Classes, subjects, timetables, grading and results',
    items: [
      { id: 'class-management',      label: 'Classes',            icon: '🎓' },
      { id: 'class-teacher-linking', label: 'Subject', icon: '🔗' },
      { id: 'timetable-management',  label: 'Timetable',          icon: '📅' },
      { id: 'grade-review',          label: 'Grade Review',        icon: '📝' },
      { id: 'grades',               label: 'Grade Analytics',     icon: '📈' },
      { id: 'report-cards',          label: 'Report Cards',        icon: '📄' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: '👥',
    color: '#4b5563',
    description: 'Users, credentials, clubs and announcements',
    items: [
      { id: 'user-management',     label: 'User Management', icon: '👥' },
      { id: 'password-management', label: 'Passwords',        icon: '🔐' },
      { id: 'club-management',     label: 'Clubs',            icon: '🏆' },
      { id: 'announcements',       label: 'Announcements',    icon: '📢' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: '💰',
    color: '#059669',
    description: 'Fee tracking and student billing',
    items: [
      { id: 'fee-structure', label: 'Fee Structure Setup', icon: '🧾' },
      { id: 'fee-history', label: 'Collections', icon: '📜' },
      { id: 'fee-report', label: 'Fee Report', icon: '📊' },
    ],
  },
  {
    id: 'analytics',
    label: 'Admin',
    icon: '📊',
    color: '#7c3aed',
    description: 'Performance data, reporting and audit trails',
    items: [
      { id: 'audit-log', label: 'Audit Log',            icon: '🔍' },
      { id: 'term-configuration', label: 'Term Configuration', icon: '📅' },
      { id: 'boarding-controls', label: 'Boarding Controls',   icon: '🔒' },
    ],
  },
];

const getActiveSubsystem = (s: AdminSection): SubsystemId | null => {
  for (const g of NAV_GROUPS) {
    if (g.items.some(i => i.id === s)) return g.id;
  }
  return null;
};

// ── End subsystem groupings ──────────────────────────────────────────────────

// Note: FormLevel is now just a number since classes are dynamic
type FormLevel = number;

interface FormData {
  id?: string;
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  form?: number;
}

type TeacherAssignments = Record<FormLevel, Record<string, number | null>>;
type FormSubjects = Record<FormLevel, string[]>;

/** Seed subject lists for every class level 1..max (TLC 8, KBA 4). */
const buildInitialFormSubjectsForMax = (max: number): FormSubjects => {
  return Array.from({ length: max }, (_, i) => i + 1).reduce((acc, form) => {
    // Start empty; only persisted DB subjects or explicitly added subjects should appear.
    acc[form] = [];
    return acc;
  }, {} as FormSubjects);
};

const buildInitialTeacherAssignments = (formSubjects: FormSubjects): TeacherAssignments => {
  return Object.keys(formSubjects).map(Number).reduce((formAcc, form) => {
    formAcc[form] = formSubjects[form].reduce((subjectAcc, subject) => {
      subjectAcc[subject] = null;
      return subjectAcc;
    }, {} as Record<string, number | null>);
    return formAcc;
  }, {} as TeacherAssignments);
};

export const AdminDashboard: React.FC = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const { currentSchool, getSchoolInfo, isSwitchingSchool, schoolSwitchId, completeSchoolSwitch } = useSchool();
  const { isMobileMenuOpen, isMobileView, toggleMobileMenu, closeMobileMenu } = useMobileMenu();
  const adminName = user?.name ?? 'John Doe';
  const schoolInfo = getSchoolInfo(currentSchool);
  const classLevelMax: number = currentSchool === 'TLC' ? 8 : 4;

  /** Next level for a new class, clamped to 1..classLevelMax (prevents level > max blocking HTML form submit). */
  const getSuggestedNextClassLevel = (): number => {
    if (classes.length === 0) return 1;
    const maxExisting = Math.max(...classes.map((c) => c.level));
    return Math.min(classLevelMax, Math.max(1, maxExisting + 1));
  };

  /** Every level 1..classLevelMax already has a class. */
  const areAllClassLevelSlotsFilled = (): boolean => {
    if (classes.length < classLevelMax) return false;
    const assigned = new Set(classes.map((c) => c.level));
    for (let l = 1; l <= classLevelMax; l++) {
      if (!assigned.has(l)) return false;
    }
    return true;
  };

  const renderLearnMore = (label: string, items: string[]) => (
    <div className={styles.learnMoreTrigger}>
      <button type="button" aria-label={label} className={styles.learnMoreButton}>?</button>
      <div className={styles.learnMoreTooltip}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
          {items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </div>
  );

  const [section, setSection] = useState<AdminSection>('dashboard');
  const [isSectionTransitioning, setIsSectionTransitioning] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FormLevel>(1); // Will be updated once classes are loaded
  const [showUserForm, setShowUserForm] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  
  // Profile state
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>(user?.phone_number ?? '');
  const [recoveryEmail, setRecoveryEmail] = useState<string>(
    (user as any)?.email ?? (user as any)?.recovery_email ?? ''
  );
  const [savingProfile, setSavingProfile] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [newUserRole, setNewUserRole] = useState<'student' | 'teacher' | 'admin'>('student');
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [predictedNewLoginId, setPredictedNewLoginId] = useState<string>('Loading...');
  const [userActionTarget, setUserActionTarget] = useState<UserDto | null>(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState<UserDto | null>(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    gender: 'male',
    dateOfBirth: '',
    form: 1
  });
  // Role editing for existing users is disabled by design (create-user role selection remains).

  // Announcements state
  const [announcements, setAnnouncements] = useState<AnnouncementDto[]>([]);
  const announcementLastSeenKey = user?.id ? `announcementLastSeen:admin:${user.id}` : null;
  const getLastSeenAnnouncementTime = (): number => {
    if (!announcementLastSeenKey) return 0;
    const raw = window.localStorage.getItem(announcementLastSeenKey);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const markAnnouncementsAsSeen = () => {
    if (!announcementLastSeenKey) return;
    window.localStorage.setItem(announcementLastSeenKey, String(Date.now()));
  };

  const countNewAnnouncements = (items: Array<{ created_at: string }>): number => {
    const lastSeen = getLastSeenAnnouncementTime();
    if (lastSeen <= 0) return 0;
    return items.reduce((count, item) => {
      const createdAt = new Date(item.created_at).getTime();
      return Number.isFinite(createdAt) && createdAt > lastSeen ? count + 1 : count;
    }, 0);
  };
  const newAnnouncementCount = countNewAnnouncements(announcements);
  const bellBadgeCount = newAnnouncementCount > 99 ? '99+' : String(newAnnouncementCount);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<number | null>(null);
  const [announcementTargetFilter, setAnnouncementTargetFilter] = useState<'all' | 'student' | 'teacher' | 'admin' | 'everyone' | 'clubs'>('all');
  const [newAnnouncement, setNewAnnouncement] = useState<CreateAnnouncementPayload>({
    title: '',
    message: '',
    target_group: 'student',
    club_id: null,
  });
  const [sendEmail, setSendEmail] = useState(false);

  // User management filter state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<'all' | 'student' | 'teacher' | 'admin'>('all');
  const [userStudentCategoryFilter, setUserStudentCategoryFilter] = useState<'all' | 'boarder' | 'day_scholar'>('all');
  const [includeInactiveUsers, setIncludeInactiveUsers] = useState(false);
  const [showGraduatedOnly, setShowGraduatedOnly] = useState(false);
  const [showStudentImport, setShowStudentImport] = useState(false);

  // Subject management state - per form (keys 1..classLevelMax for current school)
  const [formSubjects, setFormSubjects] = useState<FormSubjects>(() =>
    buildInitialFormSubjectsForMax(currentSchool === 'TLC' ? 8 : 4)
  );
  const [newSubjectName, setNewSubjectName] = useState('');

  // Teacher assignments
  const [teacherAssignments, setTeacherAssignments] = useState<TeacherAssignments>(() =>
    buildInitialTeacherAssignments(buildInitialFormSubjectsForMax(currentSchool === 'TLC' ? 8 : 4))
  );
  const [existingTeacherSubjects, setExistingTeacherSubjects] = useState<TeacherSubjectDto[]>([]);
  const [subjects, setSubjects] = useState<SubjectDto[]>([]);
  const [savingAssignments, setSavingAssignments] = useState<Record<string, boolean>>({});
  const [savingAll, setSavingAll] = useState(false);
  const teacherSubjectStateSchoolRef = React.useRef<SchoolCode | null>(null);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [auditLogFilters, setAuditLogFilters] = useState<AuditLogFilters>({
    date_from: '',
    date_to: '',
    user_filter: '',
    page: 1
  });
  const [auditLogPagination, setAuditLogPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0
  });

  // Grades analytics state
  const [gradeAnalytics, setGradeAnalytics] = useState<GradeAnalyticsDto | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [showTopPerformersModal, setShowTopPerformersModal] = useState(false);

  // Grade Review state (kept for backward compatibility with useEffect)
  const [gradeReviewForm] = useState<number>(1);
  const [gradeReviewTerm] = useState<string>('Term 1 2025');
  const [showWorstPerformersModal, setShowWorstPerformersModal] = useState(false);

  // Timetable state
  const [timetables, setTimetables] = useState<TimetableDto[]>([]);
  const [loadingTimetables, setLoadingTimetables] = useState(false);
  const [generatingTimetable, setGeneratingTimetable] = useState(false);
  const [publishingTimetable, setPublishingTimetable] = useState(false);
  const [clearingTimetable, setClearingTimetable] = useState(false);
  const [timetableForm, setTimetableForm] = useState<FormLevel>(1);
  const [conflictMessages, setConflictMessages] = useState<Record<number, TimetableConflict[]>>({});
  const [timetableViewMode, setTimetableViewMode] = useState<'student' | 'teacher'>('student');
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | null>(null);

  /** Subjects allowed in timetable create/edit: linked teacher–subject rows (+ any subject already on loaded slots). */
  const timetableLinkSubjects = useMemo((): SubjectDto[] => {
    const byId = new Map<number, SubjectDto>();
    const add = (s?: { id: number; name: string; code?: string }) => {
      if (!s?.id) return;
      if (!byId.has(s.id)) {
        byId.set(s.id, {
          id: s.id,
          name: s.name,
          code: s.code ?? '',
          school: currentSchool,
        });
      }
    };
    if (timetableViewMode === 'teacher' && selectedTeacherId) {
      existingTeacherSubjects
        .filter((ts) => ts.teacher_id === selectedTeacherId && ts.subject)
        .forEach((ts) => add(ts.subject));
    } else {
      existingTeacherSubjects
        .filter((ts) => ts.form === timetableForm && ts.subject)
        .forEach((ts) => add(ts.subject));
    }
    timetables.forEach((t) => add(t.subject));
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [currentSchool, existingTeacherSubjects, selectedTeacherId, timetableForm, timetableViewMode, timetables]);

  const timetableLinkedTeacherBySubjectId = useMemo<Record<number, number>>(() => {
    const mapping: Record<number, number> = {};
    existingTeacherSubjects
      .filter((ts) => ts.form === timetableForm && ts.subject_id && ts.teacher_id)
      .forEach((ts) => {
        mapping[ts.subject_id] = ts.teacher_id;
      });
    return mapping;
  }, [existingTeacherSubjects, timetableForm]);

  // Password management state
  const [passwordSearchTerm, setPasswordSearchTerm] = useState('');
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<UserDto | null>(null);
  const [userPasswordInfo, setUserPasswordInfo] = useState<UserPasswordInfo | null>(null);
  const [loadingPasswordInfo, setLoadingPasswordInfo] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetRequests, setPasswordResetRequests] = useState<PasswordResetRequestDto[]>([]);
  const [loadingPasswordResetRequests, setLoadingPasswordResetRequests] = useState(false);

  // Class management state
  const [classes, setClasses] = useState<SchoolClassDto[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClassId, setEditingClassId] = useState<number | null>(null);
  const [classFormData, setClassFormData] = useState<CreateSchoolClassPayload>({
    name: '',
    short_name: '',
    level: 1,
    description: '',
    status: 'active',
  });

  // Class Management – student list & progression state
  const [selectedClassForStudents, setSelectedClassForStudents] = useState<SchoolClassDto | null>(null);
  const [classStudents, setClassStudents] = useState<ClassStudentDto[]>([]);
  const [loadingClassStudents, setLoadingClassStudents] = useState(false);
  const [classStudentSearch, setClassStudentSearch] = useState('');
  const [selectedStudentIdsForProgress, setSelectedStudentIdsForProgress] = useState<number[]>([]);
  const [bulkProgressTargetClassId, setBulkProgressTargetClassId] = useState<number | null>(null);

  // Club Management state
  const [clubs, setClubs] = useState<ClubDto[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [showClubForm, setShowClubForm] = useState(false);
  const [editingClubId, setEditingClubId] = useState<number | null>(null);
  const [clubFormData, setClubFormData] = useState<CreateClubPayload>({
    name: '',
    description: '',
    status: 'active',
  });
  const [showClubMembers, setShowClubMembers] = useState(false);
  const [selectedClubForMembers, setSelectedClubForMembers] = useState<ClubDto | null>(null);
  const [clubMembers, setClubMembers] = useState<ClubMemberDto[]>([]);
  const [loadingClubMembers, setLoadingClubMembers] = useState(false);
  const [clubDrawerWidth, setClubDrawerWidth] = useState(420);
  const [isResizingClubDrawer, setIsResizingClubDrawer] = useState(false);
  const [selectedTeacherIdForClub, setSelectedTeacherIdForClub] = useState<number | ''>('');
  const [selectedStudentIdForClub, setSelectedStudentIdForClub] = useState<number | ''>('');
  const seenSchoolFetchRef = useRef(false);
  const schoolSwitchTimeoutRef = useRef<number | null>(null);

  // System settings state (term management + boarding)
  const [schoolSettings, setSchoolSettings] = useState<SchoolSettingsDto | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingBoardingLock, setSavingBoardingLock] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [boardingLockDraft, setBoardingLockDraft] = useState(false);
  const [settingsForm, setSettingsForm] = useState<{
    current_academic_year: string;
    current_term: number;
    term_start_date: string;
    term_end_date: string;
    next_term_opening: string;
    boarding_locked: boolean;
  }>({
    current_academic_year: new Date().getFullYear().toString(),
    current_term: 1,
    term_start_date: '',
    term_end_date: '',
    next_term_opening: '',
    boarding_locked: false,
  });
  const [showAdvanceTermModal, setShowAdvanceTermModal] = useState(false);
  const [advancingTerm, setAdvancingTerm] = useState(false);
  const [exportingTermData, setExportingTermData] = useState(false);
  const [advanceTermConfirmed, setAdvanceTermConfirmed] = useState(false);
  // Boarding override state
  const [boardingStudentSearch, setBoardingStudentSearch] = useState('');
  const [boardingFilterForm, setBoardingFilterForm] = useState<number | 'all'>('all');
  const [boardingFilterStatus, setBoardingFilterStatus] = useState<'all' | 'boarder' | 'day_scholar' | 'unset'>('all');
  const [boardingFilterSource, setBoardingFilterSource] = useState<'all' | 'admin' | 'student' | 'unset'>('all');
  const [selectedBoardingStudentIds, setSelectedBoardingStudentIds] = useState<number[]>([]);
  const [applyingBulkBoarding, setApplyingBulkBoarding] = useState(false);
  const [boardingStatusMap, setBoardingStatusMap] = useState<Record<number, AdminBoardingStatusEntryDto>>({});

  // Computed statistics from real data
  const totalUsers = users.length;
  const studentUsers = users.filter(u => u.role === 'student').length;
  const adminUsers = users.filter(u => u.role === 'admin').length;
  const teacherAccounts = useMemo(() => users.filter(u => u.role === 'teacher'), [users]);
  const teacherUsers = teacherAccounts.length;
  const anySchoolLoading =
    loadingUsers ||
    loadingClasses ||
    loadingClubs ||
    loadingAuditLogs ||
    loadingAnalytics ||
    loadingTimetables ||
    loadingClassStudents ||
    loadingClubMembers;

  useEffect(() => {
    if (!isSwitchingSchool) {
      seenSchoolFetchRef.current = false;
      if (schoolSwitchTimeoutRef.current !== null) {
        window.clearTimeout(schoolSwitchTimeoutRef.current);
        schoolSwitchTimeoutRef.current = null;
      }
      return;
    }

    if (anySchoolLoading) {
      seenSchoolFetchRef.current = true;
    }

    if (seenSchoolFetchRef.current && !anySchoolLoading) {
      completeSchoolSwitch(schoolSwitchId);
      return;
    }

    if (schoolSwitchTimeoutRef.current === null) {
      schoolSwitchTimeoutRef.current = window.setTimeout(() => {
        completeSchoolSwitch(schoolSwitchId);
        schoolSwitchTimeoutRef.current = null;
      }, 6000);
    }
  }, [isSwitchingSchool, schoolSwitchId, anySchoolLoading, completeSchoolSwitch]);

  useEffect(() => {
    loadAnnouncements();
    loadUsers();
    loadSubjects();
    loadActiveClassesForSelectors();
    loadClubs(); // Load clubs for announcement creation
    loadSchoolSettings();
  }, [
    currentSchool,
    announcementTargetFilter,
    includeInactiveUsers,
    showGraduatedOnly,
    userStudentCategoryFilter,
    userRoleFilter,
    schoolSettings?.current_academic_year,
    schoolSettings?.current_term,
  ]); // Reload when school, inactive, or student category (API) filters change

  useEffect(() => {
    if (userRoleFilter !== 'student') {
      setUserStudentCategoryFilter('all');
    }
  }, [userRoleFilter]);

  // Load active classes for use in selectors throughout the app
  const loadActiveClassesForSelectors = async () => {
    const max = currentSchool === 'TLC' ? 8 : 4;
    try {
      const data = await fetchActiveClasses({ school: currentSchool });
      setClasses(data);
      const activeLevels = data.filter((c) => c.status === 'active').map((c) => c.level);
      setSelectedForm((prev) => {
        if (activeLevels.length === 0) return Math.min(prev, max);
        if (prev > max || !activeLevels.includes(prev)) return activeLevels[0];
        return prev;
      });
      setTimetableForm((prev) => {
        if (activeLevels.length === 0) return Math.min(prev, max);
        if (prev > max || !activeLevels.includes(prev)) return activeLevels[0];
        return prev;
      });
    } catch (error) {
      console.error('Failed to load classes', error);
    }
  };

  useEffect(() => {
    if (section === 'class-teacher-linking') {
      void loadTeacherSubjects();
    }
  }, [section, currentSchool, classLevelMax]);

  useEffect(() => {
    if (section === 'class-management') {
      loadClasses();
    }
  }, [section, currentSchool]);

  useEffect(() => {
    if (section === 'club-management') {
      loadClubs();
    }
  }, [section, currentSchool]);

  useEffect(() => {
    if (section === 'audit-log') {
      loadAuditLogs();
    }
    if (section === 'grades') {
      loadGradeAnalytics();
    }
    if (section === 'timetable-management') {
      loadTimetables();
    }
    if (section === 'grade-review') {
      loadGradeSubmissionStatus();
    }
    if (section === 'password-management') {
      loadPasswordResetRequests();
    }
    if (section === 'term-configuration' || section === 'boarding-controls' || section === 'system-settings') {
      loadSchoolSettings();
    }
    if (section === 'dashboard') {
      // Keep hub term indicator fresh
      loadSchoolSettings();
    }
  }, [section, selectedYear, timetableForm, timetableViewMode, selectedTeacherId, auditLogFilters.page, gradeReviewForm, gradeReviewTerm, currentSchool]);

  useEffect(() => {
    if (!isResizingClubDrawer) return;

    const onMove = (event: MouseEvent) => {
      const computedWidth = window.innerWidth - event.clientX;
      const nextWidth = Math.min(760, Math.max(360, computedWidth));
      setClubDrawerWidth(nextWidth);
    };
    const onUp = () => setIsResizingClubDrawer(false);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizingClubDrawer]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const params: Parameters<typeof fetchUsers>[0] = {
        school: currentSchool,
      };
      if (showGraduatedOnly) {
        params.status = 'graduated';
      } else {
        params.include_inactive = includeInactiveUsers;
      }
      if (
        userStudentCategoryFilter !== 'all' &&
        userRoleFilter === 'student' &&
        schoolSettings
      ) {
        params.boarding_status = userStudentCategoryFilter;
        params.academic_year = schoolSettings.current_academic_year;
        params.term = schoolSettings.current_term;
      }
      const data = await fetchUsers(params);
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadSchoolSettings = async () => {
    setLoadingSettings(true);
    try {
      const s = await fetchSchoolSettings(currentSchool);
      setSchoolSettings(s);
      setSettingsForm({
        current_academic_year: s.current_academic_year,
        current_term: s.current_term,
        term_start_date: s.term_start_date ?? '',
        term_end_date: s.term_end_date ?? '',
        next_term_opening: s.next_term_opening ?? '',
        boarding_locked: s.boarding_locked,
      });
      setBoardingLockDraft(s.boarding_locked);
    } catch (error) {
      console.error('Failed to load school settings', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveTermConfiguration = async () => {
    setSavingSettings(true);
    setSettingsStatus(null);
    try {
      const s = await updateSchoolSettings(currentSchool, {
        current_academic_year: settingsForm.current_academic_year,
        current_term: settingsForm.current_term,
        term_start_date: settingsForm.term_start_date || null,
        term_end_date: settingsForm.term_end_date || null,
        next_term_opening: settingsForm.next_term_opening || null,
        boarding_locked: schoolSettings?.boarding_locked ?? settingsForm.boarding_locked,
      });
      setSchoolSettings(s);
      setSettingsStatus({ type: 'success', message: 'Term configuration saved successfully.' });
    } catch (error: any) {
      setSettingsStatus({ type: 'error', message: error?.response?.data?.message || 'Failed to save term configuration' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleApplyBoardingLock = async () => {
    setSavingBoardingLock(true);
    setSettingsStatus(null);
    try {
      const s = await updateSchoolSettings(currentSchool, {
        current_academic_year: settingsForm.current_academic_year,
        current_term: settingsForm.current_term,
        term_start_date: settingsForm.term_start_date || null,
        term_end_date: settingsForm.term_end_date || null,
        next_term_opening: settingsForm.next_term_opening || null,
        boarding_locked: boardingLockDraft,
      });
      setSchoolSettings(s);
      setSettingsForm((prev) => ({ ...prev, boarding_locked: s.boarding_locked }));
      setBoardingLockDraft(s.boarding_locked);
      setSettingsStatus({ type: 'success', message: 'Boarding lock changes applied.' });
      await loadBoardingStatusMap();
    } catch (error: any) {
      setSettingsStatus({ type: 'error', message: error?.response?.data?.message || 'Failed to apply boarding lock' });
    } finally {
      setSavingBoardingLock(false);
    }
  };

  const handleExportTermData = async () => {
    setExportingTermData(true);
    try {
      const data = await exportTermData(currentSchool);
      const schoolName = getSchoolInfo(currentSchool)?.name || currentSchool;
      const doc = buildTermExportPdf(data, schoolName);
      doc.save(`${currentSchool}_Term${data.term}_${data.academic_year}_export.pdf`);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to export term data');
    } finally {
      setExportingTermData(false);
    }
  };

  const handleAdvanceTerm = async () => {
    setAdvancingTerm(true);
    try {
      const result = await advanceSchoolTerm(currentSchool);
      alert(
        result.message +
        `\nGrades cleared: ${result.cleared.grades}` +
        `\nAssignments cleared: ${result.cleared.assignments}` +
        `\nGraduated: ${result.cleared.graduated ?? 0}` +
        `\nPromoted: ${result.cleared.promoted ?? 0}` +
        `\nFee accounts opened: ${result.cleared.fee_accounts_opened ?? 0}`
      );
      setShowAdvanceTermModal(false);
      setAdvanceTermConfirmed(false);
      await loadSchoolSettings();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to advance term');
    } finally {
      setAdvancingTerm(false);
    }
  };

  const handleSetSelectedBoarding = async (status: 'day_scholar' | 'boarder') => {
    if (!schoolSettings) {
      alert(
        'School settings could not be loaded. Reload the page or open System Settings again, then try setting boarding status.'
      );
      return;
    }
    if (selectedBoardingStudentIds.length === 0) {
      alert('Select at least one student first.');
      return;
    }
    setApplyingBulkBoarding(true);
    try {
      await Promise.all(
        selectedBoardingStudentIds.map((studentId) =>
          adminSetBoardingStatus({
            student_id: studentId,
            academic_year: schoolSettings.current_academic_year,
            term: schoolSettings.current_term,
            boarding_status: status,
          })
        )
      );
      const label = status === 'boarder' ? 'Boarder' : 'Day scholar';
      alert(
        `${label} set for ${selectedBoardingStudentIds.length} selected student(s) for Term ${schoolSettings.current_term} (${schoolSettings.current_academic_year}).`
      );
      setSelectedBoardingStudentIds([]);
      await loadUsers();
      await loadBoardingStatusMap();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to set boarding status');
    } finally {
      setApplyingBulkBoarding(false);
    }
  };

  const handleClearSelectedBoardingStatuses = async () => {
    if (!schoolSettings) return;
    if (selectedBoardingStudentIds.length === 0) {
      alert('Select at least one student first.');
      return;
    }
    setApplyingBulkBoarding(true);
    try {
      const result = await adminClearBoardingStatuses({
        school: currentSchool,
        academic_year: schoolSettings.current_academic_year,
        term: schoolSettings.current_term,
        student_ids: selectedBoardingStudentIds,
      });
      alert(result.message + ` (${result.cleared} records cleared)`);
      setSelectedBoardingStudentIds([]);
      await loadUsers();
      await loadBoardingStatusMap();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to clear selected boarding statuses');
    } finally {
      setApplyingBulkBoarding(false);
    }
  };

  const loadBoardingStatusMap = useCallback(async () => {
    if (!schoolSettings) {
      setBoardingStatusMap({});
      return;
    }
    try {
      const data = await fetchAdminBoardingStatuses({
        school: currentSchool,
        academic_year: schoolSettings.current_academic_year,
        term: schoolSettings.current_term,
      });
      const mapped: Record<number, AdminBoardingStatusEntryDto> = {};
      Object.entries(data.statuses || {}).forEach(([studentId, status]) => {
        mapped[Number(studentId)] = status;
      });
      setBoardingStatusMap(mapped);
    } catch {
      setBoardingStatusMap({});
    }
  }, [schoolSettings, currentSchool]);

  useEffect(() => {
    if (section !== 'boarding-controls' && section !== 'system-settings') return;
    if (!schoolSettings) return;
    loadBoardingStatusMap();
  }, [section, schoolSettings, loadBoardingStatusMap]);

  useEffect(() => {
    if (!showUserForm) return;
    let cancelled = false;

    const loadNextLoginId = async () => {
      try {
        setPredictedNewLoginId('Loading...');
        const nextId = await fetchNextUserLoginId(newUserRole, currentSchool);
        if (!cancelled) {
          setPredictedNewLoginId(nextId || 'Not available');
        }
      } catch {
        if (!cancelled) {
          setPredictedNewLoginId('Not available');
        }
      }
    };

    loadNextLoginId();
    return () => {
      cancelled = true;
    };
  }, [showUserForm, newUserRole, currentSchool]);

  const handleSearchUserForPassword = async (userId: number) => {
    setLoadingPasswordInfo(true);
    setUserPasswordInfo(null);
    try {
      const info = await getUserPasswordInfo(userId);
      setUserPasswordInfo(info);
      setSelectedUserForPassword(info.user as any);
    } catch (error: any) {
      console.error('Failed to load user password info', error);
      alert(error?.response?.data?.message || 'Failed to load user password information');
    } finally {
      setLoadingPasswordInfo(false);
    }
  };

  const loadPasswordResetRequests = async () => {
    setLoadingPasswordResetRequests(true);
    try {
      const requests = await fetchPasswordResetRequests();
      setPasswordResetRequests(requests);
    } catch (error) {
      console.error('Failed to load password reset requests', error);
    } finally {
      setLoadingPasswordResetRequests(false);
    }
  };

  const handleApprovePasswordReset = async (requestId: number) => {
    try {
      await approvePasswordReset(requestId);
      await loadPasswordResetRequests();
      await loadUsers();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to approve password reset request');
    }
  };

  const handleRejectPasswordReset = async (requestId: number) => {
    try {
      await rejectPasswordReset(requestId);
      await loadPasswordResetRequests();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to reject password reset request');
    }
  };

  const handleResetUserPassword = async (userId: number) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password to the default (myschool1)? The user will be required to change it on next login.')) {
      return;
    }
    setResettingPassword(true);
    try {
      const info = await resetUserPassword(userId);
      setUserPasswordInfo(info);
      alert('Password has been reset to default (myschool1).');
    } catch (error: any) {
      console.error('Failed to reset password', error);
      alert(error?.response?.data?.message || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Profile handlers
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange: React.ChangeEventHandler<HTMLInputElement> = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('File size must be less than 2MB');
      return;
    }
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(file.type) && (!fileExtension || !validExtensions.includes(fileExtension))) {
      setAvatarError('File must be an image (JPEG, PNG, GIF, or WebP)');
      return;
    }
    
    setAvatarError(null);
    const localUrl = URL.createObjectURL(file);
    setAvatarPreview(localUrl);
    setPendingAvatarFile(file);
  };

  const handleAvatarSave = async () => {
    if (!pendingAvatarFile) return;
    const currentPreview = avatarPreview;
    setAvatarError(null);
    try {
      const updatedUser = await uploadAvatar(pendingAvatarFile);
      setUser(updatedUser);
      setAvatarPreview(updatedUser.avatar_url ?? currentPreview);
      setPendingAvatarFile(null);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.errors?.avatar?.[0] ||
                          'Failed to upload avatar. Please try again.';
      setAvatarError(errorMessage);
      console.error('Failed to update avatar', err);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }
    setAvatarPreview(null);
    setPendingAvatarFile(null);
    // Note: You may want to add an API endpoint to remove the avatar
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const updatedUser = await updateUserProfile({
        phone_number: phoneNumber || undefined,
        email: recoveryEmail || undefined,
      });
      setUser(updatedUser);
      if (updatedUser.phone_number !== undefined) {
        setPhoneNumber(updatedUser.phone_number || '');
      }
      const updatedEmail = (updatedUser as any)?.email ?? (updatedUser as any)?.recovery_email ?? '';
      setRecoveryEmail(updatedEmail);
      window.alert('Profile updated successfully!');
    } catch (err: any) {
      console.error('Failed to update profile', err);
      const errorMessage = err.response?.data?.message || 'Failed to update profile. Please try again.';
      window.alert(errorMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  // Initialize profile data from user
  useEffect(() => {
    if (user?.avatar_url) {
      setAvatarPreview(user.avatar_url);
    } else {
      setAvatarPreview(null);
    }
    setPendingAvatarFile(null);
  }, [user?.id, user?.avatar_url]);

  useEffect(() => {
    if (user?.phone_number !== undefined) {
      setPhoneNumber(user.phone_number || '');
    }
    const nextEmail = (user as any)?.email ?? (user as any)?.recovery_email ?? '';
    setRecoveryEmail(nextEmail);
  }, [user?.id, user?.phone_number, (user as any)?.email, (user as any)?.recovery_email]);

  const loadAnnouncements = async () => {
    try {
      let data: AnnouncementDto[] = [];

      if (announcementTargetFilter === 'clubs') {
        data = await fetchAnnouncements({ school: currentSchool, type: 'club' });
      } else if (announcementTargetFilter === 'all') {
        const [regularAnnouncements, clubAnnouncements] = await Promise.all([
          fetchAnnouncements({ school: currentSchool, type: 'regular' }),
          fetchAnnouncements({ school: currentSchool, type: 'club' }),
        ]);
        data = [...regularAnnouncements, ...clubAnnouncements].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      } else {
        const targetGroup = announcementTargetFilter === 'everyone' ? 'all' : announcementTargetFilter;
        data = await fetchAnnouncements({
          school: currentSchool,
          type: 'regular',
          target_group: targetGroup,
        });
      }

      setAnnouncements(data);
    } catch (error) {
      console.error('Failed to load announcements', error);
    }
  };

  const handleCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newAnnouncement.target_group === 'clubs' && !newAnnouncement.club_id) {
      alert('Please select a club for club announcements.');
      return;
    }
    try {
      if (editingAnnouncementId) {
        // Update existing announcement
        await updateAnnouncement(editingAnnouncementId, newAnnouncement);
        await loadAnnouncements();
        setEditingAnnouncementId(null);
      } else {
        // Create new announcement
        const result = await createAnnouncement({ 
          ...newAnnouncement, 
          school: currentSchool,
          send_email: sendEmail 
        });
        await loadAnnouncements();
        
        // Show email sending result if emails were sent
        if (sendEmail && result.emails_sent !== undefined) {
          const emailMsg = `Announcement created! ${result.emails_sent} email(s) sent successfully.`;
          const failMsg = (result.emails_failed && result.emails_failed > 0) ? ` ${result.emails_failed} failed.` : '';
          alert(emailMsg + failMsg);
        }
      }
      setNewAnnouncement({ title: '', message: '', target_group: 'student', club_id: null });
      setSendEmail(false);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to save announcement', error);
      alert('Failed to save announcement');
    }
  };

  const handleEditAnnouncement = (announcement: AnnouncementDto) => {
    setNewAnnouncement({
      title: announcement.title,
      message: announcement.message,
      target_group: announcement.club_id ? 'clubs' : announcement.target_group,
      club_id: announcement.club_id || null,
    });
    setEditingAnnouncementId(announcement.id);
    setSendEmail(false); // Reset email checkbox when editing
    setShowCreateForm(true);
  };

  const handleDeleteAnnouncement = async (id: number) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await deleteAnnouncement(id);
      await loadAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement', error);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Posted just now';
    if (diffMins < 60) return `Posted ${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `Posted ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `Posted ${diffDays} days ago`;
    return `Posted on ${date.toLocaleDateString()}`;
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCreatingUser) return;
    setIsCreatingUser(true);

    const attemptCreateUser = async (allowDuplicate = false) => {
      const payload: any = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        role: newUserRole,
        school: currentSchool, // Use current school context
        gender: formData.gender,
        date_of_birth: formData.dateOfBirth,
        allow_duplicate: allowDuplicate,
      };

      // Backend will auto-generate ID, but we need to send form for students
      if (newUserRole === 'student') {
        payload.form = formData.form;
      }

      return createUser(payload);
    };

    try {
      let createdUser = await attemptCreateUser(false);
      setUsers(prev => {
        const filtered = prev.filter(u => u.id !== createdUser.id);
        return [createdUser, ...filtered];
      });
      // Kick off a fresh fetch in background to stay in sync with server ordering
      loadUsers();
      setShowUserForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        gender: 'male',
        dateOfBirth: '',
        form: 1
      });
      setNewUserRole('student');
    } catch (error: any) {
      console.error('Failed to create user', error);

      if (error.response?.data?.code === 'POSSIBLE_DUPLICATE' && Array.isArray(error.response?.data?.duplicates)) {
        const duplicates = error.response.data.duplicates as Array<{
          name?: string;
          login_id?: string | null;
          student_id?: string | null;
          form?: number | null;
          status?: string | null;
        }>;

        const duplicateDetails = duplicates
          .map((d, index) => {
            const identity = d.student_id || d.login_id || 'No ID';
            const form = d.form ? formatClassLevelLabel(currentSchool, d.form) : 'No form';
            const status = d.status || 'unknown';
            return `${index + 1}. ${d.name || 'Unknown'} - ${identity} - ${form} - ${status}`;
          })
          .join('\n');

        const shouldProceed = window.confirm(
          `Possible duplicate student records found:\n\n${duplicateDetails}\n\nDo you want to continue and create this student anyway?`
        );

        if (shouldProceed) {
          try {
            const createdUser = await attemptCreateUser(true);
            setUsers(prev => {
              const filtered = prev.filter(u => u.id !== createdUser.id);
              return [createdUser, ...filtered];
            });
            loadUsers();
            setShowUserForm(false);
            setFormData({
              firstName: '',
              lastName: '',
              gender: 'male',
              dateOfBirth: '',
              form: 1
            });
            setNewUserRole('student');
            return;
          } catch (retryError: any) {
            error = retryError;
          }
        } else {
          return;
        }
      }

      let errorMsg = 'Failed to create user. Please try again.';
      
      if (error.response?.data?.errors) {
        // Laravel validation errors
        const errors = error.response.data.errors;
        errorMsg = Object.values(errors).flat().join('\n');
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      alert(errorMsg);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleDeactivateUser = async (id: number) => {
    try {
      const result = await deleteUser(id);
      await loadUsers();
      alert(result.message || 'User account marked inactive.');
    } catch (error: any) {
      console.error('Failed to deactivate user', error);
      const msg = error?.response?.data?.message || 'Failed to deactivate user. Please try again.';
      alert(msg);
    }
  };

  const handlePermanentDeleteUser = async (id: number) => {
    try {
      const result = await permanentlyDeleteUser(id);
      await loadUsers();
      alert(result.message || 'User permanently deleted.');
    } catch (error: any) {
      console.error('Failed to permanently delete user', error);
      const msg = error?.response?.data?.message || 'Failed to permanently delete user. Please try again.';
      alert(msg);
    }
  };

  const handleOpenUserDetails = async (id: number) => {
    setLoadingUserDetails(true);
    setSelectedUserDetails(null);
    try {
      const details = await fetchUserById(id);
      setSelectedUserDetails(details);
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Failed to load user details');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  // Note: role editing for existing users is intentionally disabled.

  const handleTeacherAssignmentChange = (form: FormLevel, subject: string, teacherId: number | null) => {
    setTeacherAssignments(prev => ({
      ...prev,
      [form]: {
        ...(prev[form] ?? {}),
        [subject]: teacherId,
      },
    }));
  };

  // Load subjects and teacher-subject assignments
  const loadSubjects = async () => {
    try {
      const data = await fetchSubjects({ school: currentSchool });
      setSubjects(data);
    } catch (error) {
      console.error('Failed to load subjects', error);
    }
  };

  // Class management functions
  const loadClasses = async () => {
    setLoadingClasses(true);
    try {
      const data = await fetchClasses({ school: currentSchool });
      setClasses(data);
    } catch (error) {
      console.error('Failed to load classes', error);
      alert('Failed to load classes. Please try again.');
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleClassFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingClassId) {
        // Update existing class
        const result = await updateClass(editingClassId, { ...classFormData, school: currentSchool });
        alert(result.message);
      } else {
        // Create new class
        const result = await createClass({ ...classFormData, school: currentSchool });
        alert(result.message);
      }
      
      // Reload classes and reset form
      await loadClasses();
      setShowClassForm(false);
      setEditingClassId(null);
      setClassFormData({
        name: '',
        short_name: '',
        level: 1,
        description: '',
        status: 'active',
      });
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to save class';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleEditClass = (classItem: SchoolClassDto) => {
    setEditingClassId(classItem.id);
    setClassFormData({
      name: classItem.name,
      short_name: classItem.short_name || '',
      level: classItem.level,
      description: classItem.description || '',
      status: classItem.status,
    });
    setShowClassForm(true);
  };

  const handleDeleteClass = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone and will fail if students are assigned to this class.`)) {
      return;
    }

    try {
      const result = await deleteClass(id);
      alert(result.message);
      await loadClasses();
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to delete class';
      alert(errorMsg);
    }
  };

  const openClassStudents = async (classItem: SchoolClassDto) => {
    setSelectedClassForStudents(classItem);
    setLoadingClassStudents(true);
    setSelectedStudentIdsForProgress([]);
    try {
      const data: ClassStudentsResponse = await fetchClassStudentsByClassId(classItem.id, {
        search: classStudentSearch || undefined,
      });
      setClassStudents(data.students);
    } catch (error) {
      console.error('Failed to load class students', error);
      alert('Failed to load students for this class. Please try again.');
    } finally {
      setLoadingClassStudents(false);
    }
  };

  const reloadSelectedClassStudents = async () => {
    if (!selectedClassForStudents) return;
    await openClassStudents(selectedClassForStudents);
  };

  const toggleStudentSelectionForProgress = (studentId: number) => {
    setSelectedStudentIdsForProgress((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
    );
  };

  const clearStudentSelectionForProgress = () => {
    setSelectedStudentIdsForProgress([]);
  };

  const handleBulkProgress = async (action: 'promote' | 'repeat' | 'move') => {
    if (selectedStudentIdsForProgress.length === 0) {
      alert('Please select at least one student first.');
      return;
    }

    let targetClassId: number | null = null;

    if (action === 'move') {
      if (!bulkProgressTargetClassId) {
        alert('Please select a target class for move.');
        return;
      }
      targetClassId = bulkProgressTargetClassId;
    }

    const confirmMessage =
      action === 'promote'
        ? `Promote ${selectedStudentIdsForProgress.length} student(s) to the next class?`
        : action === 'repeat'
        ? `Mark ${selectedStudentIdsForProgress.length} student(s) as repeat (they will remain in the same class)?`
        : `Move ${selectedStudentIdsForProgress.length} student(s) to the selected class?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await bulkProgressStudents({
        student_ids: selectedStudentIdsForProgress,
        action,
        target_class_id: targetClassId,
      });

      alert(result.message + ` Affected: ${result.affected}, Skipped: ${result.skipped_student_ids.length}.`);
      await reloadSelectedClassStudents();
      await loadClasses();
      clearStudentSelectionForProgress();
    } catch (error: any) {
      console.error('Failed to bulk progress students', error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.errors ||
        'Failed to bulk progress students';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  // Club Management functions
  const loadClubs = async () => {
    setLoadingClubs(true);
    try {
      const data = await fetchClubs({ school: currentSchool });
      setClubs(data);
    } catch (error) {
      console.error('Failed to load clubs', error);
      alert('Failed to load clubs. Please try again.');
    } finally {
      setLoadingClubs(false);
    }
  };

  const openClubMembers = async (club: ClubDto) => {
    setSelectedClubForMembers(club);
    setShowClubMembers(true);
    setLoadingClubMembers(true);
    try {
      const data = await fetchClubMembers(club.id);
      setClubMembers(data.members);
    } catch (error: any) {
      console.error('Failed to load club members', error);
      alert(error.response?.data?.error || 'Failed to load club members. Please try again.');
    } finally {
      setLoadingClubMembers(false);
    }
  };

  const refreshSelectedClubMembers = async () => {
    if (!selectedClubForMembers) return;
    setLoadingClubMembers(true);
    try {
      const data = await fetchClubMembers(selectedClubForMembers.id);
      setClubMembers(data.members);
    } catch (error: any) {
      console.error('Failed to refresh club members', error);
      alert(error.response?.data?.error || 'Failed to refresh club members. Please try again.');
    } finally {
      setLoadingClubMembers(false);
    }
  };

  const handleAddTeacherToClub = async () => {
    if (!selectedClubForMembers || !selectedTeacherIdForClub) return;
    try {
      const result = await addClubMember(selectedClubForMembers.id, Number(selectedTeacherIdForClub), 'teacher');
      alert(result.message);
      setSelectedTeacherIdForClub('');
      await refreshSelectedClubMembers();
    } catch (error: any) {
      console.error('Failed to add teacher to club', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to add teacher to club';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleAddStudentToClub = async () => {
    if (!selectedClubForMembers || !selectedStudentIdForClub) return;
    try {
      const result = await addClubMember(selectedClubForMembers.id, Number(selectedStudentIdForClub), 'student');
      alert(result.message);
      setSelectedStudentIdForClub('');
      await refreshSelectedClubMembers();
    } catch (error: any) {
      console.error('Failed to add student to club', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to add student to club';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleRemoveClubMember = async (userId: number, name: string) => {
    if (!selectedClubForMembers) return;
    if (!window.confirm(`Remove ${name} from ${selectedClubForMembers.name}?`)) {
      return;
    }
    try {
      const result = await removeClubMember(selectedClubForMembers.id, userId);
      alert(result.message);
      await refreshSelectedClubMembers();
    } catch (error: any) {
      console.error('Failed to remove club member', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to remove club member';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleUpdateClubMemberRole = async (userId: number, newRole: string) => {
    if (!selectedClubForMembers) return;
    try {
      const result = await updateClubMemberRole(selectedClubForMembers.id, userId, newRole);
      alert(result.message);
      await refreshSelectedClubMembers();
    } catch (error: any) {
      console.error('Failed to update club member role', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to update club member role';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleClubFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingClubId) {
        const result = await updateClub(editingClubId, { ...clubFormData, school: currentSchool });
        alert(result.message);
      } else {
        const result = await createClub({ ...clubFormData, school: currentSchool });
        alert(result.message);
      }
      
      await loadClubs();
      setShowClubForm(false);
      setEditingClubId(null);
      setClubFormData({
        name: '',
        description: '',
        status: 'active',
      });
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.response?.data?.errors || 'Failed to save club';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const handleEditClub = (club: ClubDto) => {
    setEditingClubId(club.id);
    setClubFormData({
      name: club.name,
      description: club.description || '',
      status: club.status,
    });
    setShowClubForm(true);
  };

  const handleDeleteClub = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will also delete all club announcements.`)) {
      return;
    }

    try {
      const result = await deleteClub(id);
      alert(result.message);
      await loadClubs();
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to delete club';
      alert(errorMsg);
    }
  };

  const loadTeacherSubjects = async () => {
    try {
      const data = await fetchTeacherSubjects(undefined, undefined, currentSchool);
      setExistingTeacherSubjects(data);
      teacherSubjectStateSchoolRef.current = currentSchool;
      // Always derive linking state from server truth to prevent stale rehydration after deletes.
      setFormSubjects(() => {
        const merged: FormSubjects = {};
        for (let form = 1; form <= classLevelMax; form++) {
          merged[form] = [];
        }
        data.forEach((ts) => {
          if (ts.subject && ts.form >= 1 && ts.form <= classLevelMax) {
            const form = ts.form as FormLevel;
            const subjectName = ts.subject.name;
            if (!merged[form].includes(subjectName)) {
              merged[form].push(subjectName);
            }
          }
        });
        return merged;
      });
      setTeacherAssignments(() => {
        const merged: TeacherAssignments = {};
        for (let form = 1; form <= classLevelMax; form++) {
          merged[form] = {};
        }
        data.forEach((ts) => {
          if (ts.subject && ts.form >= 1 && ts.form <= classLevelMax) {
            const form = ts.form as FormLevel;
            merged[form][ts.subject.name] = ts.teacher_id ?? null;
          }
        });
        return merged;
      });
    } catch (error) {
      console.error('Failed to load teacher-subject assignments', error);
    }
  };

  const loadAuditLogs = async () => {
    setLoadingAuditLogs(true);
    try {
      const data = await fetchAuditLogs(auditLogFilters);
      // API returns a paginated object; only store the log entries for display
      setAuditLogs(data?.data || []);
      // Store pagination info
      setAuditLogPagination({
        current_page: data.current_page || 1,
        last_page: data.last_page || 1,
        per_page: data.per_page || 50,
        total: data.total || 0
      });
    } catch (error) {
      console.error('Failed to load audit logs', error);
      setAuditLogs([]); // Ensure we always have an array even on error
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  const handleFilterAuditLogs = () => {
    setAuditLogFilters({ ...auditLogFilters, page: 1 });
    // loadAuditLogs will be called via useEffect when filters change
  };

  const loadGradeAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const data = await fetchGradeAnalytics(selectedYear, currentSchool);
      setGradeAnalytics(data);
    } catch (error) {
      console.error('Failed to load grade analytics', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadGradeSubmissionStatus = async () => {
    try {
      await getGradeSubmissionStatus(gradeReviewForm, gradeReviewTerm, currentSchool);
    } catch (error) {
      console.error('Failed to load grade submission status', error);
    }
  };

  const loadTimetables = async () => {
    // #region agent log
    debugTimetableLog('loadTimetables called', { timetableViewMode, selectedTeacherId, timetableForm }, 'H1', 'pre-fix');
    // #endregion
    setLoadingTimetables(true);
    try {
      if (timetableViewMode === 'teacher' && selectedTeacherId) {
        const data = await fetchTimetables(undefined, selectedTeacherId, undefined, currentSchool);
        setTimetables(data);
      } else {
        const data = await fetchTimetables(timetableForm, undefined, undefined, currentSchool);
        setTimetables(data);
      }
      setConflictMessages({});
    } catch (error) {
      console.error('Failed to load timetables', error);
      alert('Failed to load timetables');
    } finally {
      setLoadingTimetables(false);
    }
  };

  const handleGenerateTimetable = async () => {
    // Check if there are any saved teacher-subject assignments for this form
    const formAssignments = existingTeacherSubjects.filter(ts => ts.form === timetableForm);
    if (formAssignments.length === 0) {
      alert(`No teacher-subject records found for ${formatClassLevelLabel(currentSchool, timetableForm)}.\n\nPlease go to "Class - Teacher linking" section, select ${formatClassLevelLabel(currentSchool, timetableForm)}, assign teachers to subjects, and click "Save All" before generating the timetable.`);
      return;
    }

    if (!window.confirm(`Generate a new timetable for ${formatClassLevelLabel(currentSchool, timetableForm)}? This will replace existing draft timetables.\n\nUsing ${formAssignments.length} teacher-subject record(s) from the Class-teacher linking section.`)) {
      return;
    }
    setGeneratingTimetable(true);
    try {
      const result = await generateTimetable(timetableForm, currentSchool);
      await loadTimetables();
      alert(result.message);
    } catch (error: any) {
      console.error('Failed to generate timetable', error);
      const errorMsg = error.response?.data?.message || 'Failed to generate timetable.';
      if (errorMsg.includes('No teacher-subject records')) {
        alert(`${errorMsg}\n\nPlease go to "Class - Teacher linking" section, select ${formatClassLevelLabel(currentSchool, timetableForm)}, assign teachers to subjects, and click "Save All" before generating.`);
      } else {
        alert(errorMsg);
      }
    } finally {
      setGeneratingTimetable(false);
    }
  };

  const handlePublishTimetable = async () => {
    if (!window.confirm(`Publish timetable for ${formatClassLevelLabel(currentSchool, timetableForm)} to all teachers and students?`)) {
      return;
    }
    setPublishingTimetable(true);
    try {
      await publishTimetable(timetableForm, currentSchool);
      await loadTimetables();
      alert('Timetable published successfully to all users!');
    } catch (error) {
      console.error('Failed to publish timetable', error);
      alert('Failed to publish timetable');
    } finally {
      setPublishingTimetable(false);
    }
  };

  const handleClearTimetable = async () => {
    if (!window.confirm(`Clear all timetables for ${formatClassLevelLabel(currentSchool, timetableForm)}? This action cannot be undone.`)) {
      return;
    }
    setClearingTimetable(true);
    try {
      const result = await clearTimetable(timetableForm, currentSchool);
      await loadTimetables();
      alert(result.message || 'Timetable cleared successfully!');
    } catch (error: any) {
      console.error('Failed to clear timetable', error);
      alert(error.response?.data?.message || 'Failed to clear timetable');
    } finally {
      setClearingTimetable(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const normalizeSubjectName = (name: string): string => name.trim().replace(/\s+/g, ' ').toUpperCase();

  const isDuplicateSubjectCodeError = (error: any): boolean => {
    const codeErrors = error?.response?.data?.errors?.code;
    if (Array.isArray(codeErrors)) {
      return codeErrors.some((msg: string) => String(msg).toLowerCase().includes('already exists'));
    }
    return false;
  };

  const buildSubjectCodeBase = (subjectName: string): string => {
    const cleaned = normalizeSubjectName(subjectName).replace(/[^A-Z0-9 ]/g, '');
    const tokens = cleaned.split(' ').filter(Boolean);
    const initials = tokens.map((t) => t[0]).join('');
    const compact = tokens.join('');
    const preferred = `${initials}${compact}`.replace(/[^A-Z0-9]/g, '');
    return (preferred || compact || 'SUBJECT').slice(0, 12);
  };

  const buildSubjectCodeCandidate = (base: string, attempt: number): string => {
    if (attempt === 0) return base;
    const suffix = String(attempt);
    const maxBaseLength = Math.max(1, 50 - suffix.length);
    return `${base.slice(0, maxBaseLength)}${suffix}`;
  };

  // Get or create subject by name (with robust duplicate-code retries)
  const getOrCreateSubject = async (subjectName: string): Promise<number> => {
    const normalizedTarget = normalizeSubjectName(subjectName);
    let allSubjects = subjects;
    let subject = allSubjects.find((s) => normalizeSubjectName(s.name) === normalizedTarget);
    if (subject) return subject.id;

    // Refresh once before creating to avoid stale cache collisions.
    allSubjects = await fetchSubjects({ school: currentSchool });
    setSubjects(allSubjects);
    subject = allSubjects.find((s) => normalizeSubjectName(s.name) === normalizedTarget);
    if (subject) return subject.id;

    const baseCode = buildSubjectCodeBase(subjectName);
    const maxAttempts = 200;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const candidateCode = buildSubjectCodeCandidate(baseCode, attempt);
      try {
        const created = await createSubject({
          name: subjectName.trim(),
          code: candidateCode,
          school: currentSchool,
        });
        setSubjects((prev) => {
          if (prev.some((s) => s.id === created.id)) return prev;
          return [...prev, created];
        });
        return created.id;
      } catch (error: any) {
        if (isDuplicateSubjectCodeError(error)) {
          continue;
        }
        const detail = error?.response?.data?.message || error?.message || 'Unknown validation error';
        throw new Error(`Subject create failed for "${subjectName}": ${detail}`);
      }
    }

    // Final check in case subject was created by another request while retrying.
    allSubjects = await fetchSubjects({ school: currentSchool });
    setSubjects(allSubjects);
    subject = allSubjects.find((s) => normalizeSubjectName(s.name) === normalizedTarget);
    if (subject) return subject.id;

    throw new Error(`Subject code collision exhausted for "${subjectName}".`);
  };

  // Save teacher assignment for a specific subject
  const handleSaveTeacherAssignment = async (form: FormLevel, subject: string) => {
    const teacherId = teacherAssignments[form]?.[subject];
    const key = `${form}-${subject}`;
    
    if (!teacherId) {
      alert('Please select a teacher first');
      return;
    }

    setSavingAssignments(prev => ({ ...prev, [key]: true }));

    try {
      // Get or create the subject
      const subjectId = await getOrCreateSubject(subject);

      // Check if assignment already exists
      const existing = existingTeacherSubjects.find(
        ts => ts.subject_id === subjectId && ts.form === form
      );

      if (existing) {
        // Update existing assignment if teacher changed
        if (existing.teacher_id !== teacherId) {
          await updateTeacherSubject(existing.id, { teacher_id: teacherId });
          setExistingTeacherSubjects(prev =>
            prev.map(ts => ts.id === existing.id ? { ...ts, teacher_id: teacherId } : ts)
          );
        }
      } else {
        // Create new assignment
        const newAssignment = await createTeacherSubject({
          teacher_id: teacherId,
          subject_id: subjectId,
          form: form,
          school: currentSchool,
        });
        setExistingTeacherSubjects(prev => [...prev, newAssignment]);
      }

      alert('Teacher assignment saved successfully!');
    } catch (error: any) {
      console.error('Failed to save teacher assignment', error);
      alert(`Failed to save assignment: ${error.response?.data?.message || error.message}`);
    } finally {
      setSavingAssignments(prev => ({ ...prev, [key]: false }));
    }
  };

  // Save all teacher assignments for the current form
  const handleSaveAllAssignments = async () => {
    const selectedFormSnapshot = selectedForm;
    const assignmentsForFormSnapshot = { ...(teacherAssignments[selectedFormSnapshot] ?? {}) };
    const subjectsForFormSnapshot = new Set(formSubjects[selectedFormSnapshot] ?? []);
    const assignmentsToSave: Array<{ teacher_id: number; subject_id: number; form: number }> = [];
    const failedSubjects: Array<{ subject: string; reason: string }> = [];

    // Collect all assignments that have a teacher selected
    // Create subjects in database if they don't exist
    for (const [subject, teacherId] of Object.entries(assignmentsForFormSnapshot)) {
      if (teacherId) {
        if (!subjectsForFormSnapshot.has(subject)) {
          failedSubjects.push({
            subject,
            reason: 'subject was removed from this class before save',
          });
          continue;
        }
        try {
          // Get or create the subject in the database
          const subjectId = await getOrCreateSubject(subject);
          assignmentsToSave.push({
            teacher_id: teacherId,
            subject_id: subjectId,
            form: selectedFormSnapshot
          });
        } catch (error) {
          console.error(`Failed to get/create subject: ${subject}`, error);
          failedSubjects.push({
            subject,
            reason: error instanceof Error ? error.message : 'validation error',
          });
        }
      }
    }

    if (assignmentsToSave.length === 0) {
      alert('No assignments to save. Please select at least one teacher.');
      return;
    }

    setSavingAll(true);

    try {
      const response = await bulkSaveTeacherSubjects({ assignments: assignmentsToSave, school: currentSchool });
      // Reload from backend while preserving unsaved local rows.
      await loadSubjects();
      await loadTeacherSubjects();

      const apiErrorCount = Array.isArray(response.errors) ? response.errors.length : 0;
      if (failedSubjects.length > 0 || apiErrorCount > 0) {
        const localFailureText =
          failedSubjects.length > 0
            ? `\nLocal subject setup failures:\n- ${failedSubjects
                .map((f) => `${f.subject}: ${f.reason.toLowerCase().includes('collision') ? 'code collision exhausted' : 'validation error'}`)
                .join('\n- ')}`
            : '';
        alert(`Subjects were assigned, but some entries had errors (${apiErrorCount + failedSubjects.length}).${localFailureText}`);
      } else {
        alert(`Successfully assigned subjects!`);
      }
    } catch (error: any) {
      console.error('Failed to save assignments', error);
      alert(`Failed to save assignments: ${error.response?.data?.message || error.message}`);
    } finally {
      setSavingAll(false);
    }
  };

  // Render sections
  const renderDashboard = () => {
    const now = new Date();
    const announcementsByRecency = [...announcements].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bDate - aDate;
    });
    const recentAnnouncements = announcementsByRecency.slice(0, 3);

    const statHighlights = [
      { label: 'Total users', value: totalUsers, secondary: 'Across all roles' },
      { label: 'Admin users', value: adminUsers, secondary: 'System administrators' },
      { label: 'Students', value: studentUsers, secondary: 'Enrolled learners' },
      { label: 'Teachers', value: teacherUsers, secondary: 'Faculty accounts' },
    ];



    const statIcons = ['👥', '⚙️', '🎓', '👨‍🏫'];
    const statColors = [
      currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
      '#4b5563',
      '#2563eb',
      '#059669',
    ];

    return (
      <main
        style={{
          flex: 1,
          padding: '32px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
          background: '#f5f7fa',
          minHeight: '100%'
        }}
      >
        {/* School Selector Banner */}
        <div style={{
          background: currentSchool === 'KBA' ? '#1e3a5f' : '#15803d',
          borderRadius: 12,
          padding: '24px 32px',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <img
              src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'}
              alt=""
              style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }}
            />
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                {schoolInfo.name}
              </div>
              <div style={{ fontSize: 14, opacity: 0.9 }}>
                {schoolInfo.description} • Administration Panel
              </div>
              <div
                style={{
                  fontSize: 13,
                  opacity: 0.85,
                  marginTop: 8,
                  fontStyle: 'italic',
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  maxWidth: 520,
                  lineHeight: 1.45,
                }}
              >
                {SHARED_SCHOOL_MOTTO}
              </div>
            </div>
          </div>
          <SchoolSelector />
        </div>

        {/* Modern Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
              Welcome back, {adminName} 👋
            </div>
            <div style={{ fontSize: 16, color: '#6b7280' }}>
              Here's what's happening at {schoolInfo.shortName} today
            </div>
          </div>
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: '12px 20px',
              border: '1px solid #e5e7eb',
              fontSize: 14,
              color: '#6b7280',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <span style={{ fontSize: 16 }}>📅</span>
            <span>{now.toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })}</span>
          </div>
        </div>

        {/* Modern Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
          {statHighlights.map((stat, index) => (
            <div
              key={stat.label}
              style={{
                background: statColors[index],
                color: '#fff',
                borderRadius: 16,
                padding: '24px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
              }}
            >
              <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 40, opacity: 0.3 }}>
                {statIcons[index]}
              </div>
              <div style={{ fontSize: 14, letterSpacing: '0.5px', textTransform: 'uppercase', opacity: 0.9, fontWeight: 600, marginBottom: 12 }}>
                {stat.label}
              </div>
              <div style={{ fontSize: 42, fontWeight: 700, marginBottom: 8 }}>
                {stat.value.toLocaleString()}
              </div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>{stat.secondary}</div>
            </div>
          ))}
        </div>

        {/* Subsystem Hub Cards */}
        <section>
          <div style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#1f2937',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <span>🗂️</span>
            <span>Subsystems</span>
          </div>
          <div className={styles.hubGrid}>
            {NAV_GROUPS.map(group => (
              <div
                key={group.id}
                className={styles.hubCard}
                onClick={() => void handleSectionChange(group.items[0].id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void handleSectionChange(group.items[0].id); }}
              >
                <div className={styles.hubCardHeader}>
                  <div
                    className={styles.hubCardIcon}
                    style={{ background: `${group.color}20`, color: group.color }}
                  >
                    {group.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1f2937' }}>{group.label}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{group.description}</div>
                  </div>
                </div>
                <div className={styles.hubCardPills}>
                  {group.items.map(item => (
                    <span key={item.id} className={styles.hubCardPill}>{item.icon} {item.label}</span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); void handleSectionChange(group.items[0].id); }}
                  style={{
                    marginTop: 12,
                    padding: '8px 20px',
                    borderRadius: 20,
                    border: 'none',
                    background: group.color,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  Open →
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Modern Recent Announcements */}
        <section
          style={{
            background: 'white',
            borderRadius: 16,
            border: '1px solid #e5e7eb',
            padding: '28px',
            boxShadow: '0 8px 16px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 500, color: '#222' }}>Recent updates</div>
              <div style={{ fontSize: 14, color: '#777' }}>Latest announcements and broadcasts</div>
            </div>
            <button
              type="button"
              onClick={() => void handleSectionChange('announcements')}
              style={{
                padding: '10px 20px',
                borderRadius: 999,
                border: '1px solid #6d7478',
                background: 'transparent',
                color: '#6d7478',
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              Manage announcements
            </button>
          </div>
          {recentAnnouncements.length === 0 ? (
            <div style={{ padding: '16px 0', color: '#777', fontSize: 14 }}>
              No announcements have been published yet. Create one to keep the community informed.
            </div>
          ) : (
            recentAnnouncements.map(announcement => (
              <div
                key={announcement.id}
                style={{
                  padding: '16px 0',
                  borderTop: '1px solid #efefef',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                {announcement.club && (
                  <div style={{ 
                    display: 'inline-block',
                    background: '#e8f5e9',
                    color: '#2e7d32',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4,
                    width: 'fit-content'
                  }}>
                    🏆 {announcement.club.name}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 16, fontWeight: 500 }}>{announcement.title}</span>
                  <span style={{ fontSize: 12, color: '#999' }}>{formatTimeAgo(announcement.created_at)}</span>
                </div>
                <div style={{ fontSize: 14, color: '#555', lineHeight: 1.4 }}>{announcement.message}</div>
                <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Target · {announcement.target_group}
                </div>
              </div>
            ))
          )}
        </section>
      </main>
    );
  };

  const renderAnnouncements = () => {
    const announcementFormModal = showCreateForm ? (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} style={{ maxWidth: 900 }}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>
              {editingAnnouncementId ? 'Edit Announcement' : 'Create New Announcement'}
            </h2>
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => {
                setShowCreateForm(false);
                setEditingAnnouncementId(null);
                setNewAnnouncement({ title: '', message: '', target_group: 'student', club_id: null });
                setSendEmail(false);
              }}
            >
              ×
            </button>
          </div>
          <div className={styles.modalBody}>
            <form onSubmit={handleCreateAnnouncement}>
              <div style={{ 
              background: 'white',
              padding: 8,
              borderRadius: 16
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 800 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <label style={{ 
                      minWidth: 180, 
                      fontSize: 15, 
                      color: '#1f2937', 
                      fontWeight: 600 
                    }}>
                      Title *
                    </label>
                    <input
                      type="text"
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                      required
                      placeholder="Enter announcement title"
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 10,
                        fontSize: 15,
                        color: '#1f2937',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <label style={{ 
                      minWidth: 180, 
                      fontSize: 15, 
                      color: '#1f2937', 
                      fontWeight: 600 
                    }}>
                      Target Group *
                    </label>
                    <select
                      value={newAnnouncement.target_group}
                      onChange={(e) =>
                        setNewAnnouncement((prev) => ({
                          ...prev,
                          target_group: e.target.value as CreateAnnouncementPayload['target_group'],
                          club_id: e.target.value === 'clubs' ? prev.club_id : null,
                        }))
                      }
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 10,
                        fontSize: 15,
                        background: '#fff',
                        color: '#1f2937',
                        fontWeight: 500,
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    >
                      <option value="student">🎓 Students</option>
                      <option value="teacher">👨‍🏫 Teachers</option>
                      <option value="admin">⚙️ Admins</option>
                      <option value="all">📣 Everyone</option>
                      <option value="clubs">🏆 Clubs</option>
                    </select>
                  </div>

                  {newAnnouncement.target_group === 'clubs' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <label style={{ 
                      minWidth: 180, 
                      fontSize: 15, 
                      color: '#1f2937', 
                      fontWeight: 600 
                    }}>
                      Club *
                    </label>
                    <select
                      value={newAnnouncement.club_id || ''}
                      onChange={(e) => setNewAnnouncement({ 
                        ...newAnnouncement, 
                        club_id: e.target.value ? Number(e.target.value) : null 
                      })}
                      required
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 10,
                        fontSize: 15,
                        background: '#fff',
                        color: '#1f2937',
                        fontWeight: 500,
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    >
                      <option value="">Select a club</option>
                      {clubs.filter(c => c.status === 'active').map(club => (
                        <option key={club.id} value={club.id}>🏆 {club.name}</option>
                      ))}
                    </select>
                  </div>
                  )}
                  {newAnnouncement.target_group === 'clubs' && (
                  <div style={{ 
                    marginLeft: 200, 
                    fontSize: 13, 
                    color: '#6b7280',
                    marginTop: -16
                  }}>
                    Club announcements are delivered only to members of the selected club.
                  </div>
                  )}

                  {/* Send Email Checkbox */}
                  {!editingAnnouncementId && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                      <label style={{ 
                        minWidth: 180, 
                        fontSize: 15, 
                        color: '#1f2937', 
                        fontWeight: 600 
                      }}>
                        Send Email
                      </label>
                      <div style={{ 
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}>
                        <input
                          type="checkbox"
                          id="sendEmail"
                          checked={sendEmail}
                          onChange={(e) => setSendEmail(e.target.checked)}
                          style={{
                            width: 20,
                            height: 20,
                            cursor: 'pointer',
                            accentColor: '#3b6fa0'
                          }}
                        />
                        <label 
                          htmlFor="sendEmail" 
                          style={{ 
                            fontSize: 14, 
                            color: '#4b5563',
                            cursor: 'pointer'
                          }}
                        >
                          Send email notification to target recipients with valid announcement emails
                        </label>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                    <label style={{ 
                      minWidth: 180, 
                      fontSize: 15, 
                      color: '#1f2937', 
                      fontWeight: 600,
                      paddingTop: 12
                    }}>
                      Message *
                    </label>
                    <textarea
                      value={newAnnouncement.message}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                      required
                      rows={6}
                      placeholder="Enter your announcement message here..."
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 10,
                        fontSize: 15,
                        color: '#1f2937',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 12,
                  marginTop: 32,
                  paddingTop: 32,
                  borderTop: '2px solid #f3f4f6'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingAnnouncementId(null);
                      setNewAnnouncement({ title: '', message: '', target_group: 'student', club_id: null });
                      setSendEmail(false);
                    }}
                    style={{
                      padding: '14px 32px',
                      background: '#f3f4f6',
                      color: '#6b7280',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e5e7eb';
                      e.currentTarget.style.color = '#1f2937';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.color = '#6b7280';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '14px 48px',
                      background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                    }}
                  >
                    {editingAnnouncementId ? '✓ Update Announcement' : '📢 Publish Announcement'}
                  </button>
                </div>
              </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    ) : null;

    return (
      <div style={{ 
        padding: '32px 40px', 
        flex: 1,
        background: '#f5f7fa',
        minHeight: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 32 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: 28, 
              fontWeight: 700,
              color: '#1f2937',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <span style={{ fontSize: 32 }}>📢</span>
              <span>Announcements</span>
            </h2>
            {renderLearnMore('Learn more about Announcements', [
              'Use Create Announcement to send notices to selected audiences.',
              'Set the target group carefully before publishing.',
              'Keep messages short and date-relevant so they remain useful.',
            ])}
            <p style={{ margin: 0, fontSize: 15, color: '#6b7280' }}>
              Manage announcements and notifications for the school
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              padding: '12px 24px',
              background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
            }}
          >
            <span style={{ fontSize: 18 }}>➕</span>
            <span>Create Announcement</span>
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'student', label: 'Students' },
            { key: 'teacher', label: 'Teachers' },
            { key: 'admin', label: 'Admins' },
            { key: 'everyone', label: 'Everyone' },
            { key: 'clubs', label: 'Clubs' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setAnnouncementTargetFilter(tab.key as typeof announcementTargetFilter)}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                border: '1px solid #d1d5db',
                background: announcementTargetFilter === tab.key ? '#1e3a5f' : '#fff',
                color: announcementTargetFilter === tab.key ? '#fff' : '#374151',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Modern Cards Layout */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {announcements.length === 0 ? (
            <div style={{
              background: 'white',
              padding: 60,
              borderRadius: 16,
              border: '1px solid #e5e7eb',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📢</div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                {announcementTargetFilter === 'clubs'
                  ? 'No club announcements yet'
                  : announcementTargetFilter === 'all'
                    ? 'No announcements yet'
                    : `No ${announcementTargetFilter === 'everyone' ? 'everyone' : announcementTargetFilter} announcements yet`}
              </div>
              <div style={{ fontSize: 15, color: '#6b7280' }}>
                {announcementTargetFilter === 'clubs'
                  ? 'Create your first club announcement to notify club members'
                  : 'Create your first announcement to keep everyone informed'}
              </div>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                style={{
                  background: 'white',
                  padding: '24px',
                  borderRadius: 16,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      fontSize: 20, 
                      fontWeight: 700, 
                      color: '#1f2937',
                      margin: 0,
                      marginBottom: 8
                    }}>
                      {announcement.title}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      {announcement.club && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          background: '#e8f5e9',
                          color: '#2e7d32'
                        }}>
                          🏆 {announcement.club.name}
                        </span>
                      )}
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        background: announcement.target_group === 'student' ? '#dbeafe' : 
                                    announcement.target_group === 'teacher' ? '#d1fae5' : 
                                    announcement.target_group === 'admin' ? '#fce7f3' :
                                    announcement.target_group === 'clubs' ? '#fef3c7' : '#e0e7ff',
                        color: announcement.target_group === 'student' ? '#1e40af' : 
                               announcement.target_group === 'teacher' ? '#065f46' : 
                               announcement.target_group === 'admin' ? '#9f1239' :
                               announcement.target_group === 'clubs' ? '#92400e' : '#3730a3'
                      }}>
                        {announcement.target_group === 'student' ? '🎓 Students' : 
                         announcement.target_group === 'teacher' ? '👨‍🏫 Teachers' : 
                         announcement.target_group === 'admin' ? '⚙️ Admins' :
                         announcement.target_group === 'clubs' ? '🏆 Clubs' : '📣 Everyone'}
                      </span>
                      <span style={{ fontSize: 13, color: '#6b7280' }}>
                        • {formatTimeAgo(announcement.created_at)}
                      </span>
                    </div>
                    <p style={{ 
                      fontSize: 15, 
                      color: '#4b5563',
                      lineHeight: 1.6,
                      margin: 0
                    }}>
                      {announcement.message}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 16 }}>
                    <button
                      onClick={() => handleEditAnnouncement(announcement)}
                      title="Edit"
                      style={{
                        width: 40,
                        height: 40,
                        background: '#f3f4f6',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 18,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#3b6fa0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteAnnouncement(announcement.id)}
                      title="Delete"
                      style={{
                        width: 40,
                        height: 40,
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: 18,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fee2e2';
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        {announcementFormModal}
      </div>
        );
  };

  const renderUserManagement = () => {
    const userFormModal = showUserForm ? (
      <div className={styles.modalOverlay}>
        <div className={styles.modal} style={{ maxWidth: 900 }}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>Create New User</h2>
            <button
              type="button"
              className={styles.modalClose}
              onClick={() => {
                setShowUserForm(false);
                setNewUserRole('student');
                setFormData({
                  firstName: '',
                  lastName: '',
                  gender: 'male',
                  dateOfBirth: '',
                  form: 1
                });
              }}
            >
              ×
            </button>
          </div>
          <div className={styles.modalBody}>
            <div style={{ 
              padding: '4px 8px', 
              flex: 1,
              background: '#f5f7fa',
              minHeight: '100%'
            }}>
          <form onSubmit={handleCreateUser}>
            <div style={{ 
              background: 'white', 
              padding: 36, 
              borderRadius: 16,
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 700 }}>
                {/* School and Auto-generated ID Preview */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 20,
                  padding: '18px 24px',
                  background: currentSchool === 'KBA' ? '#f0f9ff' : '#f0fdf4',
                  borderRadius: 12,
                  border: `1.5px solid ${currentSchool === 'KBA' ? '#bae6fd' : '#bbf7d0'}`
                }}>
                  <label style={{ 
                    minWidth: 180, 
                    fontSize: 15, 
                    color: '#1f2937', 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <img
                      src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'}
                      alt=""
                      style={{ width: 22, height: 22, objectFit: 'contain' }}
                    />
                    <span>{schoolInfo.shortName}</span>
                  </label>
                  <div style={{ 
                    flex: 1, 
                    padding: '12px 16px',
                    background: '#fff',
                    border: `2px solid ${currentSchool === 'KBA' ? '#0ea5e9' : '#22c55e'}`,
                    borderRadius: 8,
                    fontSize: 15,
                    color: currentSchool === 'KBA' ? '#0369a1' : '#15803d',
                    fontFamily: 'monospace',
                    fontWeight: 700
                  }}>
                    {predictedNewLoginId} <span style={{ color: '#6b7280', fontWeight: 500 }}>(Next available)</span>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <label style={{ 
                    minWidth: 180, 
                    fontSize: 15, 
                    color: '#1f2937', 
                    fontWeight: 600 
                  }}>
                    Role *
                  </label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value as any)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 15,
                    background: '#fff',
                    color: '#1f2937',
                    fontWeight: 500,
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                >
                  <option value="student">🎓 Student</option>
                  <option value="teacher">👨‍🏫 Teacher</option>
                  <option value="admin">⚙️ Admin</option>
                </select>
              </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <label style={{ 
                    minWidth: 180, 
                    fontSize: 15, 
                    color: '#1f2937', 
                    fontWeight: 600 
                  }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="Enter first name"
                    required
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      background: '#fff',
                      color: '#1f2937',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <label style={{ 
                    minWidth: 180, 
                    fontSize: 15, 
                    color: '#1f2937', 
                    fontWeight: 600 
                  }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Enter last name"
                    required
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      background: '#fff',
                      color: '#1f2937',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <label style={{ 
                    minWidth: 180, 
                    fontSize: 15, 
                    color: '#1f2937', 
                    fontWeight: 600 
                  }}>
                    Gender *
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    required
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      background: '#fff',
                      color: '#1f2937',
                      fontWeight: 500,
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <label style={{ 
                    minWidth: 180, 
                    fontSize: 15, 
                    color: '#1f2937', 
                    fontWeight: 600 
                  }}>
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    style={{
                      width: 220,
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      background: '#fff',
                      color: '#1f2937',
                      outline: 'none',
                      colorScheme: 'light',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {newUserRole === 'student' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                    <label style={{ 
                      minWidth: 180, 
                      fontSize: 15, 
                      color: '#1f2937', 
                      fontWeight: 600 
                    }}>
                      Class/{classLevelWord(currentSchool)} *
                    </label>
                    <select
                      value={formData.form}
                      onChange={(e) => setFormData({ ...formData, form: parseInt(e.target.value) })}
                      required
                      style={{
                        width: 200,
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 10,
                        fontSize: 15,
                        background: '#fff',
                        color: '#1f2937',
                        fontWeight: 500,
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    >
                      {classes.filter(c => c.status === 'active').map(classItem => (
                        <option key={classItem.id} value={classItem.level}>
                          {classItem.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 12,
                  marginTop: 32,
                  paddingTop: 32,
                  borderTop: '2px solid #f3f4f6'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserForm(false);
                      setNewUserRole('student');
                      setFormData({
                        firstName: '',
                        lastName: '',
                        gender: 'male',
                        dateOfBirth: '',
                        form: 1
                      });
                    }}
                    style={{
                      padding: '14px 32px',
                      background: '#f3f4f6',
                      color: '#6b7280',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e5e7eb';
                      e.currentTarget.style.color = '#1f2937';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.color = '#6b7280';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingUser}
                    style={{
                      padding: '14px 48px',
                      background: isCreatingUser ? '#9ca3af' : currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: isCreatingUser ? 'not-allowed' : 'pointer',
                      boxShadow: isCreatingUser ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.12)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (isCreatingUser) return;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      if (isCreatingUser) return;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                    }}
                  >
                    {isCreatingUser ? 'Creating...' : '✓ Create User'}
                  </button>
                </div>
              </div>
            </div>
          </form>
            </div>
          </div>
        </div>
      </div>
    ) : null;

    // Filter users based on search and role filter
    const filteredUsers = users.filter(user => {
      const matchesSearch = !userSearchTerm || 
        user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.login_id?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.student_id?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.teacher_id?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase());
      
      const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
      
      return matchesSearch && matchesRole;
    });

    return (
      <div style={{ 
        padding: '32px 40px', 
        flex: 1,
        background: '#f5f7fa',
        minHeight: '100%'
      }}>
        {/* Modern Header with Stats */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 24
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: 28, 
                fontWeight: 700,
                color: '#1f2937',
                marginBottom: 8
              }}>
                👥 User Management
              </h2>
              {renderLearnMore('Learn more about User Management', [
                'Create users from the Add New User button.',
                'Use the role and search filters to find specific accounts quickly.',
                'Use the i button to view full profile details and missing fields.',
              ])}
              <p style={{ margin: 0, fontSize: 15, color: '#6b7280' }}>
                Manage all users across your school system
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={() => setShowStudentImport(true)}
              style={{
                padding: '12px 24px',
                background: '#fff',
                color: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                border: '2px solid currentColor',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Import students
            </button>
            <button
              onClick={() => setShowUserForm(true)}
              style={{
                padding: '12px 24px',
                background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
              }}
            >
              <span style={{ fontSize: 18 }}>➕</span>
              <span>Add New User</span>
            </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Total Users</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1f2937' }}>{filteredUsers.length}</div>
            </div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Students</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#4facfe' }}>
                {filteredUsers.filter(u => u.role === 'student').length}
              </div>
            </div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Teachers</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#43e97b' }}>
                {filteredUsers.filter(u => u.role === 'teacher').length}
              </div>
            </div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
            }}>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Admins</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#f093fb' }}>
                {filteredUsers.filter(u => u.role === 'admin').length}
              </div>
            </div>
          </div>
        </div>

        {/* Modern Search & Filter */}
        <div style={{ 
          background: 'white',
          padding: '20px 24px',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ 
                position: 'absolute', 
                left: 16, 
                top: '50%', 
                transform: 'translateY(-50%)',
                fontSize: 18
              }}>
                🔍
              </span>
              <input
                type="text"
                placeholder="Search by name, ID, or email..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 48px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 10,
                  fontSize: 15,
                  color: '#1f2937',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              />
            </div>
            <select
              value={userRoleFilter}
              onChange={(e) => setUserRoleFilter(e.target.value as any)}
              style={{
                minWidth: 180,
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 15,
                color: '#1f2937',
                background: '#fff',
                cursor: 'pointer',
                outline: 'none',
                fontWeight: 500
              }}
            >
              <option value="all">All Roles</option>
              <option value="student">Students</option>
              <option value="teacher">Teachers</option>
              <option value="admin">Admins</option>
            </select>
            <select
              value={userStudentCategoryFilter}
              onChange={(e) =>
                setUserStudentCategoryFilter(e.target.value as 'all' | 'boarder' | 'day_scholar')
              }
              disabled={userRoleFilter !== 'student'}
              title={
                userRoleFilter !== 'student'
                  ? 'Select Students role to use this list filter'
                  : 'Filter only: narrows the user list by current-term boarding type (from saved school settings). It does not change any student. To assign boarding, use System Settings → Boarding Status Controls.'
              }
              style={{
                minWidth: 200,
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 15,
                color: userRoleFilter !== 'student' ? '#9ca3af' : '#1f2937',
                background: userRoleFilter !== 'student' ? '#f3f4f6' : '#fff',
                cursor: userRoleFilter !== 'student' ? 'not-allowed' : 'pointer',
                outline: 'none',
                fontWeight: 500
              }}
            >
              <option value="all">Filter: All types</option>
              <option value="day_scholar">Filter: Day scholars</option>
              <option value="boarder">Filter: Boarders</option>
            </select>
            <button
              onClick={() => {
                setUserSearchTerm('');
                setUserRoleFilter('all');
                setUserStudentCategoryFilter('all');
                setShowGraduatedOnly(false);
                setIncludeInactiveUsers(false);
              }}
              style={{
                padding: '12px 24px',
                background: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e5e7eb';
                e.currentTarget.style.color = '#1f2937';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              Clear
            </button>
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#4b5563' }}>
              <input
                type="checkbox"
                checked={includeInactiveUsers}
                onChange={(e) => setIncludeInactiveUsers(e.target.checked)}
              />
              <span>Include inactive users</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#4b5563' }}>
              <input
                type="checkbox"
                checked={showGraduatedOnly}
                onChange={(e) => setShowGraduatedOnly(e.target.checked)}
              />
              <span>Graduated (read-only history)</span>
            </label>
            {includeInactiveUsers && (
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Inactive users can be reactivated but cannot log in.
              </span>
            )}
          </div>
        </div>

        {/* Modern Table */}
        <div style={{ 
          background: 'white', 
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          overflowX: 'auto',
          overflowY: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ 
            background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f', 
            padding: '18px 24px', 
            display: 'grid', 
            gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1.2fr 200px',
            gap: 20,
            minWidth: 920,
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <div>Name</div>
            <div>User ID</div>
            <div>Role</div>
            <div>Status</div>
            <div>Last Login</div>
            <div>Actions</div>
          </div>
          {loadingUsers ? (
            <div style={{ 
              padding: 60, 
              textAlign: 'center', 
              color: '#6b7280',
              fontSize: 15
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              <div>Loading users...</div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ 
              padding: 60, 
              textAlign: 'center', 
              color: '#6b7280',
              fontSize: 15
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
              <div>No users found matching your filter</div>
            </div>
          ) : (
            filteredUsers.map((user, idx) => (
              <div
                key={user.id}
                style={{
                  background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                  padding: '18px 24px',
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.2fr 1fr 1fr 1.2fr 200px',
                  gap: 20,
                  minWidth: 920,
                  alignItems: 'center',
                  fontSize: 15,
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f9fafb'}
              >
                <div style={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span style={{ fontSize: 20 }}>
                    {user.role === 'student' ? '🎓' : user.role === 'teacher' ? '👨‍🏫' : '⚙️'}
                  </span>
                  {user.name}
                </div>
                <div style={{ 
                  fontSize: 13, 
                  color: '#6b7280', 
                  fontFamily: 'monospace',
                  background: '#f3f4f6',
                  padding: '4px 8px',
                  borderRadius: 6,
                  display: 'inline-block'
                }}>
                  {user.login_id || user.student_id || user.teacher_id || `#${user.id}`}
                </div>
                <div>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    background: user.role === 'student' ? '#dbeafe' : user.role === 'teacher' ? '#d1fae5' : '#fce7f3',
                    color: user.role === 'student' ? '#1e40af' : user.role === 'teacher' ? '#065f46' : '#9f1239'
                  }}>
                    {user.role}
                  </span>
                </div>
                <div>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    background: user.status === 'inactive' ? '#fee2e2' : user.status === 'graduated' ? '#fef3c7' : '#d1fae5',
                    color: user.status === 'inactive' ? '#b91c1c' : user.status === 'graduated' ? '#92400e' : '#065f46'
                  }}>
                    ● {user.status === 'inactive' ? 'Inactive' : user.status === 'graduated' ? 'Graduated' : 'Active'}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {user.updated_at ? formatTimeAgo(user.updated_at).replace('Posted', '').trim() : 'Recently'}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleOpenUserDetails(user.id)}
                    title="View user details"
                    style={{
                      padding: '8px 12px',
                      background: '#eff6ff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                      color: '#1d4ed8',
                    }}
                  >
                    i
                  </button>
                  {/* Role editing disabled for existing users */}
                  {user.status === 'graduated' ? (
                    <span style={{ fontSize: 12, color: '#92400e', alignSelf: 'center' }}>History</span>
                  ) : user.status === 'inactive' ? (
                    <button
                      onClick={async () => {
                        try {
                          const updated = await updateUser(user.id, { status: 'active' });
                          setUsers(users.map(u => u.id === user.id ? updated : u));
                        } catch (error: any) {
                          console.error('Failed to reactivate user', error);
                          const msg = error?.response?.data?.message || 'Failed to reactivate user. Please try again.';
                          alert(msg);
                        }
                      }}
                      title="Reactivate User"
                      style={{
                        padding: '8px 14px',
                        background: '#dcfce7',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#22c55e';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#dcfce7';
                      }}
                    >
                      Reactivate
                    </button>
                  ) : (
                    <button
                      onClick={() => setUserActionTarget(user)}
                      title="Deactivate User"
                      style={{
                        padding: '8px 14px',
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: 8,
                        fontSize: 18,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fee2e2';
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {userActionTarget && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} style={{ maxWidth: 560 }}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>User Account Action</h3>
                <button
                  className={styles.modalClose}
                  onClick={() => setUserActionTarget(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className={styles.modalBody}>
                <p style={{ marginTop: 0, color: '#374151' }}>
                  Choose what to do with <strong>{userActionTarget.name}</strong> (
                  {userActionTarget.login_id || userActionTarget.student_id || userActionTarget.teacher_id || `#${userActionTarget.id}`}).
                </p>
                <p style={{ color: '#6b7280', fontSize: 14 }}>
                  Inactive keeps historical records and allows reactivation later. Permanent delete removes the account
                  and may fail if this user is still referenced by protected records.
                </p>
              </div>
              <div className={styles.modalFooter}>
                <button
                  className={styles.secondaryButton}
                  onClick={() => setUserActionTarget(null)}
                >
                  Cancel
                </button>
                <button
                  className={styles.secondaryButton}
                  onClick={async () => {
                    const targetId = userActionTarget.id;
                    setUserActionTarget(null);
                    await handleDeactivateUser(targetId);
                  }}
                >
                  Make Inactive
                </button>
                <button
                  className={styles.dangerButton}
                  onClick={async () => {
                    const confirmed = window.confirm('Permanently delete this account? This cannot be undone.');
                    if (!confirmed) return;
                    const targetId = userActionTarget.id;
                    setUserActionTarget(null);
                    await handlePermanentDeleteUser(targetId);
                  }}
                >
                  Permanently Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {(loadingUserDetails || selectedUserDetails) && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} style={{ maxWidth: 680 }}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>User Details</h3>
                <button
                  className={styles.modalClose}
                  onClick={() => {
                    setSelectedUserDetails(null);
                    setLoadingUserDetails(false);
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className={styles.modalBody}>
                {loadingUserDetails ? (
                  <div style={{ color: '#6b7280' }}>Loading user details...</div>
                ) : selectedUserDetails ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 10, columnGap: 12 }}>
                    <strong>Full Name</strong><span>{selectedUserDetails.name || 'Not added yet'}</span>
                    <strong>Login ID</strong><span>{selectedUserDetails.login_id || selectedUserDetails.student_id || selectedUserDetails.teacher_id || 'Not added yet'}</span>
                    <strong>Role</strong><span style={{ textTransform: 'capitalize' }}>{selectedUserDetails.role || 'Not added yet'}</span>
                    <strong>School</strong><span>{selectedUserDetails.school || 'Not added yet'}</span>
                    <strong>Email</strong><span>{selectedUserDetails.email || 'Not added yet'}</span>
                    <strong>Recovery Email</strong><span>{selectedUserDetails.recovery_email || 'Not added yet'}</span>
                    <strong>Announcement Email</strong><span>{selectedUserDetails.announcement_email || 'Not added yet'}</span>
                    <strong>Phone Number</strong><span>{selectedUserDetails.phone_number || 'Not added yet'}</span>
                    <strong>Gender</strong><span>{selectedUserDetails.gender || 'Not added yet'}</span>
                    <strong>Date of Birth</strong><span>{selectedUserDetails.date_of_birth || 'Not added yet'}</span>
                    <strong>Class</strong><span>{selectedUserDetails.form ? formatClassLevelLabel(selectedUserDetails.school ?? currentSchool, selectedUserDetails.form) : 'Not added yet'}</span>
                    <strong>Status</strong><span style={{ textTransform: 'capitalize' }}>{selectedUserDetails.status || 'Not added yet'}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
        {userFormModal}
      </div>
    );
  };

  const renderPasswordManagement = () => {
    // Filter users based on search term
    const filteredUsers = users.filter(user => {
      if (!passwordSearchTerm) return false;
      const searchLower = passwordSearchTerm.toLowerCase();
      return (
        user.name?.toLowerCase().includes(searchLower) ||
        user.login_id?.toLowerCase().includes(searchLower) ||
        user.student_id?.toLowerCase().includes(searchLower) ||
        user.teacher_id?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div style={{ 
        padding: '32px 40px', 
        flex: 1,
        background: '#f5f7fa',
        minHeight: '100%'
      }}>
        {/* Modern Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <h2 style={{ 
              fontSize: 28, 
              fontWeight: 700, 
              margin: 0,
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <span style={{ fontSize: 32 }}>🔐</span>
              <span>Password Management</span>
            </h2>
            {renderLearnMore('Learn more about Password Management', [
              'Search for a user by name, ID, or email to review password status.',
              'Use reset only when a user cannot access their account.',
              'Approve or reject pending reset requests from the queue below.',
            ])}
          </div>
          <p style={{ fontSize: 15, color: '#6b7280', margin: 0 }}>
            Search for users to view password status or reset passwords to default (myschool1)
          </p>
        </div>

        {/* Modern Search Section */}
        <div style={{ 
          background: 'white',
          padding: '24px',
          borderRadius: 16,
          border: '1px solid #e5e7eb',
          marginBottom: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ position: 'relative' }}>
            <span style={{ 
              position: 'absolute', 
              left: 20, 
              top: '50%', 
              transform: 'translateY(-50%)',
              fontSize: 20
            }}>
              🔍
            </span>
            <input
              type="text"
              value={passwordSearchTerm}
              onChange={(e) => setPasswordSearchTerm(e.target.value)}
              placeholder="Search by name, ID, or email..."
              style={{
                width: '100%',
                padding: '14px 20px 14px 56px',
                border: '2px solid #e5e7eb',
                borderRadius: 12,
                fontSize: 15,
                color: '#1f2937',
                outline: 'none',
                transition: 'border-color 0.2s',
                fontWeight: 500
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>

          {/* Modern Search Results */}
          {passwordSearchTerm && (
            <div style={{ 
              background: 'white', 
              border: '2px solid #e5e7eb', 
              borderRadius: 12,
              maxHeight: 450,
              overflowY: 'auto',
              marginTop: 16,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }}>
              {filteredUsers.length === 0 ? (
                <div style={{ 
                  padding: 40, 
                  textAlign: 'center', 
                  color: '#6b7280',
                  fontSize: 15
                }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                  <div>No users found matching <strong>"{passwordSearchTerm}"</strong></div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {filteredUsers.map((user, idx) => (
                    <div
                      key={user.id}
                      style={{
                        padding: '18px 20px',
                        borderBottom: idx < filteredUsers.length - 1 ? '1px solid #f3f4f6' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      onClick={() => handleSearchUserForPassword(user.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ 
                          width: 44,
                          height: 44,
                          borderRadius: 10,
                          background: user.role === 'student' ? '#dbeafe' : user.role === 'teacher' ? '#d1fae5' : '#fce7f3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20
                        }}>
                          {user.role === 'student' ? '🎓' : user.role === 'teacher' ? '👨‍🏫' : '⚙️'}
                        </div>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, color: '#1f2937' }}>
                            {user.name}
                          </div>
                          <div style={{ fontSize: 13, color: '#6b7280' }}>
                            <span style={{ 
                              fontFamily: 'monospace', 
                              background: '#f3f4f6',
                              padding: '2px 8px',
                              borderRadius: 4
                            }}>
                              {user.login_id || user.student_id || user.teacher_id}
                            </span>
                            <span style={{ margin: '0 8px' }}>•</span>
                            <span style={{ textTransform: 'capitalize' }}>{user.role}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSearchUserForPassword(user.id);
                        }}
                        style={{
                          padding: '10px 20px',
                          background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        }}
                      >
                        View Password Info →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{
          background: 'white',
          padding: '20px 24px',
          borderRadius: 16,
          border: '1px solid #e5e7eb',
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 18, color: '#1f2937' }}>Password Reset Requests</h3>
            <button className={styles.secondaryButton} onClick={loadPasswordResetRequests}>Refresh</button>
          </div>
          {loadingPasswordResetRequests ? (
            <div style={{ color: '#6b7280' }}>Loading requests...</div>
          ) : passwordResetRequests.filter((req) => req.status === 'pending').length === 0 ? (
            <div style={{ color: '#6b7280' }}>No pending password reset requests.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {passwordResetRequests
                .filter((req) => req.status === 'pending')
                .map((req) => (
                  <div
                    key={req.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: '12px 14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ fontSize: 14 }}>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>{req.name} ({req.login_id})</div>
                      <div style={{ color: '#6b7280', marginTop: 2 }}>
                        Role: {req.role} {req.reason ? `• Reason: ${req.reason}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className={styles.successButton} onClick={() => handleApprovePasswordReset(req.id)}>Approve</button>
                      <button className={styles.secondaryButton} onClick={() => handleRejectPasswordReset(req.id)}>Reject</button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Modern User Password Info Section */}
        {selectedUserForPassword && (
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 16,
            padding: 32,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: 28,
              paddingBottom: 20,
              borderBottom: '2px solid #f3f4f6'
            }}>
              <h3 style={{ 
                fontSize: 22, 
                fontWeight: 700, 
                margin: 0,
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <span style={{ fontSize: 28 }}>🔑</span>
                <span>Password Info: {selectedUserForPassword.name}</span>
              </h3>
              <button
                onClick={() => {
                  setSelectedUserForPassword(null);
                  setUserPasswordInfo(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                  e.currentTarget.style.color = '#1f2937';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                ✕ Close
              </button>
            </div>

            {loadingPasswordInfo ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#6b7280'
              }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                <div style={{ fontSize: 15 }}>Loading password information...</div>
              </div>
            ) : userPasswordInfo ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ 
                  padding: 24, 
                  background: '#f0f9ff', 
                  borderRadius: 12,
                  border: '1.5px solid #bae6fd'
                }}>
                  <div style={{ 
                    fontSize: 14, 
                    color: '#0369a1', 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 16
                  }}>
                    User Details
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, fontSize: 15 }}>
                    <div style={{ color: '#6b7280', fontWeight: 500 }}>Name:</div>
                    <div style={{ color: '#1f2937', fontWeight: 600 }}>{userPasswordInfo.user.name}</div>
                    
                    <div style={{ color: '#6b7280', fontWeight: 500 }}>Login ID:</div>
                    <div style={{ 
                      color: '#1f2937', 
                      fontWeight: 600,
                      fontFamily: 'monospace',
                      background: 'white',
                      padding: '4px 12px',
                      borderRadius: 6,
                      display: 'inline-block',
                      border: '1px solid #0ea5e9'
                    }}>
                      {userPasswordInfo.user.login_id || userPasswordInfo.user.student_id || userPasswordInfo.user.teacher_id}
                    </div>
                    
                    <div style={{ color: '#6b7280', fontWeight: 500 }}>Role:</div>
                    <div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        background: userPasswordInfo.user.role === 'student' ? '#dbeafe' : userPasswordInfo.user.role === 'teacher' ? '#d1fae5' : '#fce7f3',
                        color: userPasswordInfo.user.role === 'student' ? '#1e40af' : userPasswordInfo.user.role === 'teacher' ? '#065f46' : '#9f1239'
                      }}>
                        {userPasswordInfo.user.role}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: 24,
                  background: userPasswordInfo.is_default_password ? '#d1fae5' : '#fef3c7',
                  border: `2px solid ${userPasswordInfo.is_default_password ? '#10b981' : '#f59e0b'}`,
                  borderRadius: 12
                }}>
                  <div style={{ 
                    fontSize: 16, 
                    fontWeight: 700, 
                    marginBottom: 16,
                    color: userPasswordInfo.is_default_password ? '#065f46' : '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <span style={{ fontSize: 24 }}>
                      {userPasswordInfo.is_default_password ? '✓' : '⚠️'}
                    </span>
                    <span>Password Status</span>
                  </div>
                  {userPasswordInfo.is_default_password ? (
                    <div>
                      <div style={{ 
                        fontSize: 14, 
                        marginBottom: 16, 
                        color: '#065f46',
                        fontWeight: 500
                      }}>
                        This user is still using the default password.
                      </div>
                      <div style={{
                        padding: 16,
                        background: 'white',
                        borderRadius: 10,
                        border: '2px solid #10b981',
                        marginBottom: 12
                      }}>
                        <div style={{ 
                          fontSize: 12, 
                          color: '#6b7280', 
                          marginBottom: 8,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 600
                        }}>
                          Current Password:
                        </div>
                        <div style={{ 
                          fontSize: 22, 
                          fontWeight: 700, 
                          color: '#10b981', 
                          fontFamily: 'monospace',
                          letterSpacing: '1px'
                        }}>
                          myschool1
                        </div>
                      </div>
                      {userPasswordInfo.password_change_required && (
                        <div style={{ 
                          fontSize: 13, 
                          color: '#065f46', 
                          fontStyle: 'italic',
                          background: 'rgba(255,255,255,0.5)',
                          padding: '8px 12px',
                          borderRadius: 6
                        }}>
                          ℹ️ User will be required to change this password on next login.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div style={{ 
                        fontSize: 14, 
                        marginBottom: 12, 
                        color: '#92400e',
                        fontWeight: 500
                      }}>
                        This user has changed their password from the default.
                      </div>
                      <div style={{ 
                        fontSize: 13, 
                        color: '#92400e', 
                        background: 'rgba(255,255,255,0.5)',
                        padding: '12px 16px',
                        borderRadius: 8
                      }}>
                        🔒 For security reasons, the actual password cannot be displayed. You can reset it to the default password below.
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: 12,
                  paddingTop: 20,
                  borderTop: '2px solid rgba(0,0,0,0.05)'
                }}>
                  <button
                    onClick={() => handleResetUserPassword(selectedUserForPassword.id)}
                    disabled={resettingPassword || (userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required)}
                    style={{
                      padding: '14px 28px',
                      background: (resettingPassword || (userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required)) 
                        ? '#e5e7eb' 
                        : '#dc2626',
                      color: (resettingPassword || (userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required)) 
                        ? '#9ca3af' 
                        : '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: (resettingPassword || (userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required)) ? 'not-allowed' : 'pointer',
                      boxShadow: (resettingPassword || (userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required))
                        ? 'none'
                        : '0 4px 12px rgba(239, 68, 68, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!resettingPassword && !(userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required)) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!resettingPassword && !(userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required)) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                      }
                    }}
                  >
                    {resettingPassword ? '⏳ Resetting...' : '🔄 Reset to Default Password (myschool1)'}
                  </button>
                  {userPasswordInfo.is_default_password && !userPasswordInfo.password_change_required && (
                    <div style={{ 
                      fontSize: 13, 
                      color: '#6b7280', 
                      alignSelf: 'center',
                      background: '#f3f4f6',
                      padding: '12px 16px',
                      borderRadius: 8,
                      fontWeight: 500
                    }}>
                      ℹ️ Password is already set to default
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}

      </div>
    );
  };


  const renderClassTeacherLinking = () => {
    const assignmentsForForm = teacherAssignments[selectedForm] ?? {};
    const subjectsForForm = formSubjects[selectedForm] ?? [];
    const hasTeacherAccounts = teacherAccounts.length > 0;
    const activeClassTabs = classes.filter((c) => c.status === 'active');

    return (
      <div style={{ padding: 40, flex: 1 }}>
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 className={styles.sectionTitle}>Subjects</h2>
            {renderLearnMore('Learn more about Subjects', [
              'Pick a class tab, then add subjects for that class.',
              'Assign a teacher to each subject and use Save All to store links (required before timetable generation).',
              'Timetable editing only offers subjects that are linked here for the selected form.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>
            Add subjects and link teachers per class. These links drive timetable subject options.
          </p>
          <p className={styles.sectionSubtitle} style={{ marginTop: 6 }}>
            Removing a row here only unlinks it from this class. Global subject deletion is managed in subject management actions.
          </p>
        </div>

        {/* Modern Form Tabs */}
        <div style={{ 
          background: 'white',
          padding: '20px 32px',
          borderRadius: 16,
          marginBottom: 24,
          display: 'flex',
          gap: 12,
          justifyContent: 'center',
          flexWrap: 'wrap',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          {activeClassTabs.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 15, padding: '8px 0', width: '100%' }}>
              No classes available. Create at least one active class in{' '}
              <strong>Class Management</strong> before you can link subjects and teachers.
            </div>
          ) : (
            activeClassTabs.map((classItem) => (
            <button
              key={classItem.id}
              onClick={() => {
                if (!savingAll) setSelectedForm(classItem.level);
              }}
              disabled={savingAll}
              style={{
                background: selectedForm === classItem.level ? currentSchool === 'TLC' ? '#15803d' : '#1e3a5f' : '#f3f4f6',
                color: selectedForm === classItem.level ? '#fff' : '#6b7280',
                border: 'none',
                padding: '14px 32px',
                fontSize: 16,
                fontWeight: 600,
                cursor: savingAll ? 'not-allowed' : 'pointer',
                borderRadius: 10,
                transition: 'all 0.2s',
                boxShadow: selectedForm === classItem.level ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                opacity: savingAll ? 0.65 : 1,
              }}
              onMouseEnter={(e) => {
                if (selectedForm !== classItem.level) {
                  e.currentTarget.style.background = '#e5e7eb';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedForm !== classItem.level) {
                  e.currentTarget.style.background = '#f3f4f6';
                }
              }}
            >
              📚 {classItem.name}
            </button>
            ))
          )}
        </div>

        {/* Add subjects for this form */}
        <div style={{ 
          background: 'white', 
          padding: '24px 28px', 
          borderRadius: 16,
          marginBottom: 24,
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div style={{ 
              fontSize: 18, 
              fontWeight: 700, 
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontSize: 20 }}>📖</span>
              <span>Add Subjects</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Enter new subject name..."
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const btn = e.currentTarget.nextElementSibling as HTMLButtonElement;
                  btn?.click();
                }
              }}
              style={{
                flex: 1,
                padding: '10px 14px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 15,
              }}
            />
            <button
              onClick={() => {
                const currentFormSubjects = formSubjects[selectedForm] ?? [];
                if (newSubjectName.trim() && !currentFormSubjects.includes(newSubjectName.trim())) {
                  const newSubject = newSubjectName.trim();
                  // Add to current form's subject list
                  setFormSubjects(prev => ({
                    ...prev,
                    [selectedForm]: [...(prev[selectedForm] ?? []), newSubject]
                  }));
                  // Add to current form's teacher assignments
                  setTeacherAssignments(prev => ({
                    ...prev,
                    [selectedForm]: { ...(prev[selectedForm] ?? {}), [newSubject]: null }
                  }));
                  setNewSubjectName('');
                }
              }}
              style={{
                padding: '10px 24px',
                background: '#5a5a5a',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                fontSize: 15,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Add Subject
            </button>
          </div>
        </div>

        {/* Subject-Teacher Table */}
        <div style={{ background: '#fff', borderRadius: 4, overflowX: 'auto', overflowY: 'hidden', border: '1px solid #ddd' }}>
          <div style={{ 
            background: '#f5f5f5', 
            padding: '16px 30px', 
            borderBottom: '2px solid #666',
            minWidth: 980,
          }}>
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16, color: '#111827' }}>
                  Subject–Teacher Assignments for {formatClassLevelLabel(currentSchool, selectedForm)}
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
                  This table contains <strong>{subjectsForForm.length}</strong> subject
                  {subjectsForForm.length !== 1 ? 's' : ''} for {formatClassLevelLabel(currentSchool, selectedForm)}. Assign teachers, then use <strong>Save All</strong>.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleSaveAllAssignments}
                  disabled={savingAll}
                  style={{
                    padding: '8px 18px',
                    background: savingAll ? '#9ca3af' : '#16a34a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 999,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: savingAll ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    opacity: savingAll ? 0.7 : 1
                  }}
                >
                  {savingAll ? 'Saving…' : 'Save All'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (!selectedForm) {
                      alert('Please select a class level first.');
                      return;
                    }
                    if (!window.confirm(`This will clear all teacher–subject links for ${formatClassLevelLabel(currentSchool, selectedForm)}. Subjects will remain. Continue?`)) {
                      return;
                    }
                    try {
                      await clearTeacherSubjectsForForm({ school: currentSchool, form: selectedForm });
                      // Clear local assignments for this form, then reload from backend
                      setTeacherAssignments(prev => ({
                        ...prev,
                        [selectedForm]: {}
                      }));
                      await loadTeacherSubjects();
                      alert(`All teacher–subject links for ${formatClassLevelLabel(currentSchool, selectedForm)} have been cleared.`);
                    } catch (err: any) {
                      console.error('Failed to clear teacher-subject assignments', err);
                      alert(err?.response?.data?.message || 'Failed to clear assignments. Please try again.');
                    }
                  }}
                  style={{
                    padding: '8px 18px',
                    background: '#f97316',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 999,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1.5fr 1fr 140px',
              gap: 30,
              flex: 1,
              fontWeight: 500,
              fontSize: 16,
              textAlign: 'center'
            }}>
              <div>Subject</div>
              <div>Assign teacher</div>
              <div>Status</div>
              <div>Actions</div>
            </div>
          </div>
          {!hasTeacherAccounts ? (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 15, color: '#666' }}>
              <div style={{ marginBottom: 10 }}>No teacher accounts available yet.</div>
              <div style={{ marginBottom: 16 }}>Create teacher profiles under user management to begin linking classes.</div>
              <button
                type="button"
                onClick={() => void handleSectionChange('user-management')}
                style={{
                  padding: '10px 24px',
                  borderRadius: 4,
                  border: '1px solid #5a5a5a',
                  background: '#fff',
                  color: '#5a5a5a',
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Go to user management
              </button>
            </div>
          ) : subjectsForForm.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 15, color: '#666' }}>
              No subjects configured for {formatClassLevelLabel(currentSchool, selectedForm)} yet. Add subjects above to begin assigning teachers.
            </div>
          ) : (
            subjectsForForm.map((subject) => {
              const assignedTeacherId = assignmentsForForm?.[subject] ?? null;
              const assignedTeacher = teacherAccounts.find(t => t.id === assignedTeacherId);

              return (
                <div
                  key={subject}
                  style={{
                    background: '#fff',
                    padding: '20px 30px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.5fr 1fr 140px',
                    gap: 30,
                  minWidth: 820,
                    alignItems: 'center',
                    borderBottom: '1px solid #ddd',
                    fontSize: 15
                  }}
                >
                  <div style={{ textAlign: 'center', fontWeight: 500 }}>{subject}</div>
                  <div>
                    <select
                      value={assignedTeacherId ?? ''}
                      onChange={(e) =>
                        handleTeacherAssignmentChange(
                          selectedForm,
                          subject,
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        fontSize: 15,
                        background: '#fff',
                        color: '#444'
                      }}
                    >
                      <option value="">Select teacher</option>
                      {teacherAccounts.map(teacher => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name} ({teacher.teacher_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ textAlign: 'center', fontSize: 13, color: assignedTeacher ? '#4caf50' : '#999' }}>
                    {assignedTeacher ? 'Linked' : 'Unassigned'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    <button
                      onClick={() => handleSaveTeacherAssignment(selectedForm, subject)}
                      disabled={savingAssignments[`${selectedForm}-${subject}`] || !assignedTeacherId}
                      style={{
                        width: 36,
                        height: 36,
                        background: assignedTeacherId && !savingAssignments[`${selectedForm}-${subject}`] ? '#4caf50' : '#ccc',
                        border: 'none',
                        borderRadius: 4,
                        color: '#fff',
                        fontSize: 16,
                        cursor: assignedTeacherId && !savingAssignments[`${selectedForm}-${subject}`] ? 'pointer' : 'not-allowed',
                        opacity: assignedTeacherId && !savingAssignments[`${selectedForm}-${subject}`] ? 1 : 0.6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Save assignment"
                    >
                      {savingAssignments[`${selectedForm}-${subject}`] ? '⏳' : '✓'}
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Remove "${subject}" from ${formatClassLevelLabel(currentSchool, selectedForm)} teacher-subject links only? This does not delete the subject globally.`)) {
                          try {
                            // Delete every matching link defensively in case legacy duplicates exist.
                            const subjectObj = subjects.find(s => s.name.toLowerCase() === subject.toLowerCase());
                            if (subjectObj) {
                              const matches = existingTeacherSubjects.filter(
                                ts => ts.subject_id === subjectObj.id && ts.form === selectedForm
                              );
                              for (const existing of matches) {
                                await deleteTeacherSubject(existing.id);
                              }
                            }
                            await loadSubjects();
                            await loadTeacherSubjects();
                          } catch (error) {
                            console.error('Failed to remove subject assignment', error);
                            alert('Failed to remove subject assignment');
                          }
                        }
                      }}
                      style={{
                        width: 36,
                        height: 36,
                        background: '#d9534f',
                        border: 'none',
                        borderRadius: 4,
                        color: '#fff',
                        fontSize: 16,
                        cursor: 'pointer',
                      }}
                      title="Remove link from this form only"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderAuditLog = () => (
    <div style={{ padding: 40, flex: 1 }}>
      {/* Modern Filters */}
      <div style={{ 
        background: 'white',
        padding: '24px 28px',
        borderRadius: 16,
        marginBottom: 24,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
        <div style={{ 
          fontSize: 18, 
          fontWeight: 700, 
          color: '#1f2937',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span style={{ fontSize: 20 }}>🔎</span>
          <span>Filter Audit Logs</span>
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontSize: 14, color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>Date from:</label>
            <input
              type="date"
              value={auditLogFilters.date_from || ''}
              onChange={(e) => setAuditLogFilters({ ...auditLogFilters, date_from: e.target.value })}
              style={{
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 14,
                width: 180,
                background: '#fff',
                color: '#1f2937',
                outline: 'none',
                fontWeight: 500,
                cursor: 'pointer'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontSize: 14, color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>Date to:</label>
            <input
              type="date"
              value={auditLogFilters.date_to || ''}
              onChange={(e) => setAuditLogFilters({ ...auditLogFilters, date_to: e.target.value })}
              style={{
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 14,
                width: 180,
                background: '#fff',
                color: '#1f2937',
                outline: 'none',
                fontWeight: 500,
                cursor: 'pointer'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 250 }}>
            <label style={{ fontSize: 14, color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>User:</label>
            <input
              type="text"
              placeholder="Search by username..."
              value={auditLogFilters.user_filter || ''}
              onChange={(e) => setAuditLogFilters({ ...auditLogFilters, user_filter: e.target.value })}
              style={{
                flex: 1,
                padding: '10px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 14,
                background: '#fff',
                color: '#1f2937',
                outline: 'none'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
          </div>
          <button
            onClick={handleFilterAuditLogs}
            style={{
              padding: '12px 28px',
              background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
            }}
          >
            🔍 Apply Filters
          </button>
          <button
            onClick={() => {
              setAuditLogFilters({});
              handleFilterAuditLogs();
            }}
            style={{
              padding: '12px 24px',
            background: '#f3f4f6',
            color: '#6b7280',
            border: 'none',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e5e7eb';
            e.currentTarget.style.color = '#1f2937';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          ✕ Clear
        </button>
        </div>
      </div>

      {/* Modern Audit Log Table */}
      <div style={{ 
        background: 'white', 
        borderRadius: 16, 
        overflowX: 'auto',
        overflowY: 'hidden',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
        <div style={{ 
          background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f', 
          padding: '18px 24px', 
          display: 'grid', 
          gridTemplateColumns: '180px 140px 180px 100px 2fr',
          gap: 20,
          minWidth: 920,
          color: '#fff',
          fontWeight: 600,
          fontSize: 14,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          <div>📅 Timestamp</div>
          <div>👤 Actor</div>
          <div>⚡ Action</div>
          <div>🆔 Entity ID</div>
          <div>📋 Details + IP</div>
        </div>
        {loadingAuditLogs ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f5f5f5' }}>
            <div style={{ fontSize: 18 }}>Loading audit logs...</div>
          </div>
        ) : !auditLogs || auditLogs.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f5f5f5' }}>
            <div style={{ fontSize: 18, marginBottom: 12 }}>No audit logs available</div>
            <div style={{ fontSize: 14 }}>No activity has been logged yet. Activities will appear here as users interact with the system.</div>
          </div>
        ) : (
          auditLogs.map((log, idx) => (
            <div
              key={log.id}
              style={{
                background: idx % 2 === 0 ? '#f5f5f5' : '#e8e8e8',
                padding: '14px 20px',
                display: 'grid',
                gridTemplateColumns: '180px 140px 180px 100px 2fr',
                gap: 20,
                minWidth: 920,
                alignItems: 'center',
                fontSize: 14
              }}
            >
              <div style={{ color: '#333' }}>{formatDateTime(log.created_at)}</div>
              <div style={{ color: '#333', fontWeight: 500 }}>{log.actor || 'System'}</div>
              <div style={{ color: '#333' }}>{log.action}</div>
              <div style={{ color: '#666', fontFamily: 'monospace' }}>{log.entity_id || '-'}</div>
              <div style={{ color: '#333', fontSize: 13 }}>
                {log.details || '-'}
                {log.ip_address && (
                  <span style={{ color: '#666', marginLeft: 8 }}>(IP: {log.ip_address})</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!loadingAuditLogs && auditLogs && auditLogs.length > 0 && auditLogPagination.last_page > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 12, 
          marginTop: 24,
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => {
              if (auditLogFilters.page && auditLogFilters.page > 1) {
                setAuditLogFilters({ ...auditLogFilters, page: auditLogFilters.page - 1 });
              }
            }}
            disabled={!auditLogFilters.page || auditLogFilters.page <= 1}
            style={{
              padding: '10px 20px',
              background: (!auditLogFilters.page || auditLogFilters.page <= 1) ? '#ccc' : '#5a5a5a',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 14,
              cursor: (!auditLogFilters.page || auditLogFilters.page <= 1) ? 'not-allowed' : 'pointer',
              opacity: (!auditLogFilters.page || auditLogFilters.page <= 1) ? 0.6 : 1
            }}
          >
            Previous
          </button>

          <div style={{ 
            display: 'flex', 
            gap: 8, 
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {Array.from({ length: Math.min(7, auditLogPagination.last_page) }, (_, i) => {
              let pageNum: number;
              const currentPage = auditLogFilters.page || 1;
              const lastPage = auditLogPagination.last_page;

              // Show pages around current page
              if (lastPage <= 7) {
                pageNum = i + 1;
              } else if (currentPage <= 4) {
                pageNum = i + 1;
              } else if (currentPage >= lastPage - 3) {
                pageNum = lastPage - 6 + i;
              } else {
                pageNum = currentPage - 3 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setAuditLogFilters({ ...auditLogFilters, page: pageNum })}
                  style={{
                    padding: '10px 16px',
                    background: (auditLogFilters.page || 1) === pageNum ? '#5a5a5a' : '#fff',
                    color: (auditLogFilters.page || 1) === pageNum ? '#fff' : '#333',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    fontSize: 14,
                    cursor: 'pointer',
                    fontWeight: (auditLogFilters.page || 1) === pageNum ? 'bold' : 'normal',
                    minWidth: 40
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => {
              const currentPage = auditLogFilters.page || 1;
              if (currentPage < auditLogPagination.last_page) {
                setAuditLogFilters({ ...auditLogFilters, page: currentPage + 1 });
              }
            }}
            disabled={(auditLogFilters.page || 1) >= auditLogPagination.last_page}
            style={{
              padding: '10px 20px',
              background: (auditLogFilters.page || 1) >= auditLogPagination.last_page ? '#ccc' : '#5a5a5a',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 14,
              cursor: (auditLogFilters.page || 1) >= auditLogPagination.last_page ? 'not-allowed' : 'pointer',
              opacity: (auditLogFilters.page || 1) >= auditLogPagination.last_page ? 0.6 : 1
            }}
          >
            Next
          </button>

          <div style={{ 
            marginLeft: 16, 
            fontSize: 14, 
            color: '#666',
            whiteSpace: 'nowrap'
          }}>
            Page {auditLogFilters.page || 1} of {auditLogPagination.last_page} 
            ({auditLogPagination.total} total)
          </div>
        </div>
      )}
    </div>
  );

  const renderGrades = () => {
    if (loadingAnalytics) {
      return (
        <div style={{ 
          padding: '32px 40px', 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: '#f5f7fa',
          minHeight: '100%'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <div style={{ fontSize: 18, color: '#6b7280', fontWeight: 500 }}>Loading analytics...</div>
          </div>
        </div>
      );
    }

    if (!gradeAnalytics) {
      return (
        <div style={{ 
          padding: '32px 40px', 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: '#f5f7fa',
          minHeight: '100%'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
            <div style={{ fontSize: 18, color: '#6b7280', fontWeight: 500 }}>No analytics data available</div>
          </div>
        </div>
      );
    }

    const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0'];
    const yearComparison = gradeAnalytics.year_comparison;

    return (
      <div style={{ 
        padding: '32px 40px', 
        flex: 1, 
        background: '#f5f7fa',
        minHeight: '100%'
      }}>
        {/* Modern Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 32 
        }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: 28, 
              fontWeight: 700, 
              color: '#1f2937',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <span style={{ fontSize: 32 }}>📈</span>
              <span>Grade Analytics</span>
            </h2>
            <div style={{ fontSize: 15, color: '#6b7280' }}>
              Comprehensive performance insights and trends
            </div>
          </div>
          <div style={{ 
            background: 'white',
            padding: '12px 20px',
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            display: 'flex', 
            gap: 12, 
            alignItems: 'center' 
          }}>
            <label style={{ fontSize: 14, color: '#6b7280', fontWeight: 600 }}>Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{
                padding: '8px 14px',
                border: '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 15,
                background: '#fff',
                color: '#1f2937',
                cursor: 'pointer',
                fontWeight: 600,
                outline: 'none'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#3b6fa0'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year.toString()}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Modern Key Metrics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 32 }}>
          <div style={{ 
            background: '#2563eb', 
            borderRadius: 12, 
            padding: '28px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 40, opacity: 0.3 }}>📊</div>
            <div style={{ 
              fontSize: 13, 
              marginBottom: 12, 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              fontWeight: 600,
              opacity: 0.9
            }}>
              Current Year Average
            </div>
            <div style={{ fontSize: 42, fontWeight: 700, marginBottom: 8 }}>
              {yearComparison.current_average.toFixed(1)}
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {yearComparison.current_total_students} students
            </div>
          </div>
          <div style={{ 
            background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f', 
            borderRadius: 16, 
            padding: '28px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 40, opacity: 0.3 }}>📉</div>
            <div style={{ 
              fontSize: 13, 
              marginBottom: 12, 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              fontWeight: 600,
              opacity: 0.9
            }}>
              Previous Year Average
            </div>
            <div style={{ fontSize: 42, fontWeight: 700, marginBottom: 8 }}>
              {yearComparison.previous_average.toFixed(1)}
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {yearComparison.previous_total_students} students
            </div>
          </div>
          <div style={{ 
            background: yearComparison.is_better ? '#059669' : '#dc2626', 
            borderRadius: 12, 
            padding: '28px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 40, opacity: 0.3 }}>
              {yearComparison.is_better ? '📈' : '📉'}
            </div>
            <div style={{ 
              fontSize: 13, 
              marginBottom: 12, 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              fontWeight: 600,
              opacity: 0.9
            }}>
              Year-over-Year Change
            </div>
            <div style={{ fontSize: 42, fontWeight: 700, marginBottom: 8 }}>
              {yearComparison.is_better ? '+' : ''}{yearComparison.change_percentage.toFixed(1)}%
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {yearComparison.is_better ? 'Improved' : 'Declined'} by {Math.abs(yearComparison.change_margin).toFixed(1)} points
            </div>
          </div>
          <div style={{ 
            background: '#7c3aed', 
            borderRadius: 12, 
            padding: '28px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 40, opacity: 0.3 }}>🏆</div>
            <div style={{ 
              fontSize: 13, 
              marginBottom: 12, 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              fontWeight: 600,
              opacity: 0.9
            }}>
              Top Performers
            </div>
            <div style={{ fontSize: 42, fontWeight: 700, marginBottom: 8 }}>
              {gradeAnalytics.top_performers.length}
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              Students with highest averages
            </div>
          </div>
        </div>

        {/* Year Comparison Chart */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 30, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20, color: '#222' }}>
            Year-over-Year Performance Comparison
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { name: yearComparison.previous_year, value: yearComparison.previous_average, label: 'Previous Year' },
              { name: yearComparison.current_year, value: yearComparison.current_average, label: 'Current Year' }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#3b6fa0" name="Average Marks" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Modern Term Analytics */}
        <div style={{ 
          background: 'white', 
          borderRadius: 16, 
          padding: '28px', 
          marginBottom: 24, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            fontSize: 20, 
            fontWeight: 700, 
            marginBottom: 24, 
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <span style={{ fontSize: 24 }}>📈</span>
            <span>Semester/Term Performance Analysis</span>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={gradeAnalytics.term_analytics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="term" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="current_average" 
                stroke="#4caf50" 
                strokeWidth={3}
                name={`${yearComparison.current_year} Average`}
                dot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="previous_average" 
                stroke="#f44336" 
                strokeWidth={3}
                name={`${yearComparison.previous_year} Average`}
                strokeDasharray="5 5"
                dot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {gradeAnalytics.term_analytics.map((term, idx) => (
              <div 
                key={idx}
                style={{
                  padding: '16px',
                  background: term.change_percentage >= 0 ? '#e8f5e9' : '#ffebee',
                  borderRadius: 8,
                  border: `1px solid ${term.change_percentage >= 0 ? '#4caf50' : '#f44336'}`
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{term.term}</div>
                <div style={{ fontSize: 24, fontWeight: 300, marginBottom: 4 }}>
                  {term.current_average.toFixed(1)}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {term.change_percentage >= 0 ? '↑' : '↓'} {Math.abs(term.change_percentage).toFixed(1)}% 
                  ({term.change_margin >= 0 ? '+' : ''}{term.change_margin.toFixed(1)} points)
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {term.total_students} students
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top and Worst Performers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20, marginBottom: 30 }}>
          {/* Top Performers */}
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: '#222' }}>Top Performers</div>
              <button
                onClick={() => setShowTopPerformersModal(true)}
                style={{
                  padding: '8px 16px',
                  background: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                View More
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {gradeAnalytics.top_performers.slice(0, 5).map((performer, idx) => (
                <div
                  key={performer.student_id}
                  style={{
                    padding: '12px',
                    background: idx === 0 ? '#e8f5e9' : '#f5f5f5',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {idx + 1}. {performer.student?.name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {performer.student?.student_id || performer.student?.login_id || 'N/A'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 500, color: '#4caf50' }}>
                      {performer.average_marks.toFixed(1)}
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>avg</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Worst Performers */}
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: '#222' }}>Worst Performers</div>
              <button
                onClick={() => setShowWorstPerformersModal(true)}
                style={{
                  padding: '8px 16px',
                  background: '#f44336',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                View More
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {gradeAnalytics.worst_performers.slice(0, 5).map((performer, idx) => (
                <div
                  key={performer.student_id}
                  style={{
                    padding: '12px',
                    background: idx === 0 ? '#ffebee' : '#f5f5f5',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {idx + 1}. {performer.student?.name || 'Unknown'}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {performer.student?.student_id || performer.student?.login_id || 'N/A'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 500, color: '#f44336' }}>
                      {performer.average_marks.toFixed(1)}
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>avg</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gender Analytics */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 30, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20, color: '#222' }}>
            Gender-Based Performance Analysis
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 30 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gradeAnalytics.gender_analytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="gender" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="average_marks" fill="#6d7478" name="Average Marks" />
              </BarChart>
            </ResponsiveContainer>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={gradeAnalytics.gender_analytics}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ gender, percent }: { gender: string; percent: number }) => `${gender}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="total_students"
                  nameKey="gender"
                >
                  {gradeAnalytics.gender_analytics.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {gradeAnalytics.gender_analytics.map((gender, idx) => (
              <div key={idx} style={{ padding: '16px', background: '#f5f5f5', borderRadius: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>{gender.gender}</div>
                <div style={{ fontSize: 24, fontWeight: 300, marginBottom: 4 }}>
                  {gender.average_marks.toFixed(1)}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {gender.total_students} students
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 8, lineHeight: 1.5 }}>
                  Grades:{' '}
                  {Object.entries(gender.grade_distribution || {})
                    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Best Grades Section */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px', marginBottom: 30, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20, color: '#222' }}>
            Best Grades Attained
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {gradeAnalytics.best_grades.slice(0, 12).map((grade, idx) => {
              const formNum =
                typeof grade.form === 'number' ? grade.form : parseInt(String(grade.form), 10);
              const gForm = Number.isFinite(formNum) ? formNum : 3;
              const display = formatGradeForContext(
                currentSchool as SchoolCode,
                gForm,
                grade.marks,
                String(grade.grade)
              );
              return (
              <div
                key={idx}
                style={{
                  padding: '16px',
                  background: idx < 3 ? '#e8f5e9' : '#f5f5f5',
                  borderRadius: 8,
                  border: idx < 3 ? '2px solid #4caf50' : '1px solid #ddd'
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                  {grade.student_name}
                </div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  {grade.subject} · {formatClassLevelLabel(currentSchool, gForm)} · {grade.term}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 500, color: '#4caf50' }}>
                      {grade.marks}
                    </div>
                    <div style={{ fontSize: 11, color: '#999' }}>marks</div>
                  </div>
                  <div style={{ 
                    padding: '6px 12px', 
                    background: '#4caf50', 
                    color: '#fff', 
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: 'right',
                    lineHeight: 1.35,
                    maxWidth: 160,
                  }}>
                    {display}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>

        {/* Form and Subject Analytics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20, marginBottom: 30 }}>
          {/* Form Analytics */}
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20, color: '#222' }}>
              {classLevelWord(currentSchool)}-Wise Performance
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gradeAnalytics.form_analytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="form" tickFormatter={(v) => formatClassLevelLabel(currentSchool, Number(v))} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="average_marks" fill="#2196f3" name="Average Marks" />
                <Bar dataKey="pass_rate" fill="#4caf50" name="Pass Rate %" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {gradeAnalytics.form_analytics.map((form, idx) => (
                <div key={idx} style={{ padding: '12px', background: '#f5f5f5', borderRadius: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{formatClassLevelLabel(currentSchool, form.form)}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{form.total_students} students</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 500 }}>{form.average_marks.toFixed(1)}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{form.pass_rate.toFixed(1)}% pass</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subject Analytics */}
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 20, color: '#222' }}>
              Subject Performance
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={gradeAnalytics.subject_analytics.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="subject_name" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="average_marks" fill="#ff9800" name="Average Marks" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
              {gradeAnalytics.subject_analytics.slice(0, 10).map((subject, idx) => (
                <div key={idx} style={{ padding: '12px', background: '#f5f5f5', borderRadius: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{subject.subject_name}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{subject.total_students} students</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 500 }}>{subject.average_marks.toFixed(1)}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{subject.pass_rate.toFixed(1)}% pass</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Modals */}
        {showTopPerformersModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowTopPerformersModal(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '30px',
                maxWidth: 800,
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 500 }}>Top 10 Performers</div>
                <button
                  onClick={() => setShowTopPerformersModal(false)}
                  style={{
                    padding: '8px 16px',
                    background: '#999',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {gradeAnalytics.top_performers.map((performer, idx) => (
                  <div
                    key={performer.student_id}
                    style={{
                      padding: '16px',
                      background: idx < 3 ? '#e8f5e9' : '#f5f5f5',
                      borderRadius: 8,
                      border: idx < 3 ? '2px solid #4caf50' : '1px solid #ddd'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          #{idx + 1} {performer.student?.name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {performer.student?.student_id || performer.student?.login_id || 'N/A'} ·{' '}
                          {typeof performer.student?.form === 'number'
                            ? formatClassLevelLabel(performer.student?.school ?? currentSchool, performer.student.form)
                            : 'N/A'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 24, fontWeight: 500, color: '#4caf50' }}>
                          {performer.average_marks.toFixed(1)}
                        </div>
                        <div style={{ fontSize: 11, color: '#999' }}>average</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                      Subjects: {performer.grades.map(g => g.subject).join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {showWorstPerformersModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowWorstPerformersModal(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '30px',
                maxWidth: 800,
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 500 }}>Bottom 10 Performers</div>
                <button
                  onClick={() => setShowWorstPerformersModal(false)}
                  style={{
                    padding: '8px 16px',
                    background: '#999',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {gradeAnalytics.worst_performers.map((performer, idx) => (
                  <div
                    key={performer.student_id}
                    style={{
                      padding: '16px',
                      background: idx < 3 ? '#ffebee' : '#f5f5f5',
                      borderRadius: 8,
                      border: idx < 3 ? '2px solid #f44336' : '1px solid #ddd'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                          #{idx + 1} {performer.student?.name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {performer.student?.student_id || performer.student?.login_id || 'N/A'} ·{' '}
                          {typeof performer.student?.form === 'number'
                            ? formatClassLevelLabel(performer.student?.school ?? currentSchool, performer.student.form)
                            : 'N/A'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 24, fontWeight: 500, color: '#f44336' }}>
                          {performer.average_marks.toFixed(1)}
                        </div>
                        <div style={{ fontSize: 11, color: '#999' }}>average</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                      Subjects: {performer.grades.map(g => g.subject).join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGradeReview = () => {
    return <AdminGradeReviewSection />;
  };

  const renderClassManagement = () => {
    return (
      <div style={{ padding: 40, flex: 1 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              color: '#1f2937',
              marginBottom: 8
            }}>
              🎓 Class Management
            </h2>
            {renderLearnMore('Learn more about Class Management', [
              'Create and maintain classes in level order for promotions.',
              'Use View Students to manage progression and class movements.',
              'Keep class names and short names consistent for easier reporting.',
            ])}
            <p style={{ margin: 0, fontSize: 15, color: '#6b7280' }}>
              Configure the classes/forms available in your school system
            </p>
          </div>
          
          {!showClassForm && !areAllClassLevelSlotsFilled() && (
            <button
              onClick={() => {
                setShowClassForm(true);
                setEditingClassId(null);
                setClassFormData({
                  name: '',
                  short_name: '',
                  level: getSuggestedNextClassLevel(),
                  description: '',
                  status: 'active',
                });
              }}
              style={{
                padding: '12px 24px',
                background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <span style={{ fontSize: 18 }}>+</span>
              Add New Class
            </button>
          )}
          {!showClassForm && areAllClassLevelSlotsFilled() && (
            <p style={{ margin: 0, fontSize: 14, color: '#6b7280', maxWidth: 360, textAlign: 'right' }}>
              All {classLevelMax} class levels are in use for {currentSchool}. Remove or merge a class before adding another.
            </p>
          )}
        </div>

        {/* Class Form Modal */}
        {showClassForm && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} style={{ maxWidth: 900 }}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{editingClassId ? 'Edit Class' : 'Create New Class'}</h3>
                <button
                  type="button"
                  className={styles.modalClose}
                  onClick={() => {
                    setShowClassForm(false);
                    setEditingClassId(null);
                    setClassFormData({
                      name: '',
                      short_name: '',
                      level: 1,
                      description: '',
                      status: 'active',
                    });
                  }}
                >
                  ×
                </button>
              </div>
              <div className={styles.modalBody}>
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 8
                }}>
            <h3 style={{
              margin: '0 0 24px 0',
              fontSize: 20,
              fontWeight: 600,
              color: '#1f2937'
            }}>
              {editingClassId ? 'Edit Class' : 'Create New Class'}
            </h3>

            <form onSubmit={handleClassFormSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 24, marginBottom: 24 }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Class Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={classFormData.name}
                    onChange={(e) => setClassFormData({ ...classFormData, name: e.target.value })}
                    placeholder={currentSchool === 'TLC' ? 'e.g., Std 1, Standard 3' : 'e.g., Form 1, Form 3'}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Short Name
                  </label>
                  <input
                    type="text"
                    value={classFormData.short_name}
                    onChange={(e) => setClassFormData({ ...classFormData, short_name: e.target.value })}
                    placeholder="e.g., F1, G5, KG"
                    maxLength={10}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Level/Order *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max={classLevelMax}
                    value={classFormData.level}
                    onChange={(e) => {
                      const parsedLevel = parseInt(e.target.value, 10) || 1;
                      setClassFormData({
                        ...classFormData,
                        level: Math.min(classLevelMax, Math.max(1, parsedLevel)),
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      outline: 'none',
                    }}
                  />
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {currentSchool} supports levels 1 to {classLevelMax}.
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Status *
                  </label>
                  <select
                    value={classFormData.status}
                    onChange={(e) => setClassFormData({ ...classFormData, status: e.target.value as 'active' | 'inactive' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Description
                </label>
                <textarea
                  value={classFormData.description}
                  onChange={(e) => setClassFormData({ ...classFormData, description: e.target.value })}
                  placeholder="Optional description for this class"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 15,
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 32px',
                    background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {editingClassId ? 'Update Class' : 'Create Class'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowClassForm(false);
                    setEditingClassId(null);
                    setClassFormData({
                      name: '',
                      short_name: '',
                      level: 1,
                      description: '',
                      status: 'active',
                    });
                  }}
                  style={{
                    padding: '12px 32px',
                    background: '#fff',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Classes List */}
        {loadingClasses ? (
          <div style={{
            padding: 60,
            textAlign: 'center',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: 18, color: '#6b7280' }}>Loading classes...</div>
          </div>
        ) : classes.length === 0 ? (
          <div style={{
            padding: 60,
            textAlign: 'center',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎓</div>
            <div style={{ fontSize: 18, color: '#374151', fontWeight: 600, marginBottom: 8 }}>
              No Classes Yet
            </div>
            <div style={{ fontSize: 15, color: '#6b7280' }}>
              Click "Add New Class" to create your first class
            </div>
          </div>
        ) : (
          <div style={{
            background: '#fff',
            borderRadius: 16,
            overflowX: 'auto',
            overflowY: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Level
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Name
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Short Name
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Description
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Students
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {classes.map((classItem) => (
                  <tr key={classItem.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 24px', fontSize: 15, color: '#1f2937', fontWeight: 600 }}>
                      {classItem.level}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: 15, color: '#1f2937', fontWeight: 600 }}>
                      {classItem.name}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: 14, color: '#6b7280' }}>
                      {classItem.short_name || '-'}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: 14, color: '#6b7280' }}>
                      {classItem.description || '-'}
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        background: classItem.status === 'active' ? '#d1fae5' : '#fee2e2',
                        color: classItem.status === 'active' ? '#065f46' : '#991b1b'
                      }}>
                        {classItem.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: 14, color: '#1f2937' }}>
                      {typeof classItem.students_count === 'number' ? classItem.students_count : '—'}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => openClassStudents(classItem)}
                          style={{
                            padding: '8px 16px',
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          View Students
                        </button>
                        <button
                          onClick={() => handleEditClass(classItem)}
                          style={{
                            padding: '8px 16px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClass(classItem.id, classItem.name)}
                          style={{
                            padding: '8px 16px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Class Students List */}
        <div style={{ marginTop: 32 }}>
          <div style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
          }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>
                Students in Selected Class
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Choose a class above to view its students and perform promotions or repeats.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <select
                value={selectedClassForStudents?.id ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const found = classes.find(c => c.id === id) || null;
                  if (found) {
                    openClassStudents(found);
                  } else {
                    setSelectedClassForStudents(null);
                    setClassStudents([]);
                    setSelectedStudentIdsForProgress([]);
                  }
                }}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: 14,
                  minWidth: 220,
                  cursor: 'pointer',
                }}
              >
                <option value="">Select class…</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} (Level {cls.level})
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Search by name or ID…"
                value={classStudentSearch}
                onChange={(e) => setClassStudentSearch(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: 14,
                  minWidth: 220,
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (selectedClassForStudents) {
                    openClassStudents(selectedClassForStudents);
                  }
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#e5e7eb',
                  color: '#111827',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Refresh
              </button>
            </div>
          </div>

          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <div style={{ fontSize: 14, color: '#4b5563' }}>
                {selectedClassForStudents
                  ? `${classStudents.length} student(s) in ${selectedClassForStudents.name}`
                  : 'No class selected'}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  Selected: {selectedStudentIdsForProgress.length}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const visibleIds = classStudents.map(s => s.id);
                    setSelectedStudentIdsForProgress(visibleIds);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #d1d5db',
                    background: '#f9fafb',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={clearStudentSelectionForProgress}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  disabled={!selectedClassForStudents || selectedStudentIdsForProgress.length === 0}
                  onClick={() => handleBulkProgress('promote')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: selectedClassForStudents && selectedStudentIdsForProgress.length > 0 ? '#10b981' : '#d1fae5',
                    color: selectedClassForStudents && selectedStudentIdsForProgress.length > 0 ? '#ecfdf5' : '#6b7280',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: selectedClassForStudents && selectedStudentIdsForProgress.length > 0 ? 'pointer' : 'default',
                  }}
                >
                  Promote to Next Class
                </button>
                <button
                  type="button"
                  disabled={!selectedClassForStudents || selectedStudentIdsForProgress.length === 0}
                  onClick={() => handleBulkProgress('repeat')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: selectedClassForStudents && selectedStudentIdsForProgress.length > 0 ? '#fbbf24' : '#fef3c7',
                    color: selectedClassForStudents && selectedStudentIdsForProgress.length > 0 ? '#78350f' : '#a16207',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: selectedClassForStudents && selectedStudentIdsForProgress.length > 0 ? 'pointer' : 'default',
                  }}
                >
                  Mark as Repeat
                </button>
                <select
                  value={bulkProgressTargetClassId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : null;
                    setBulkProgressTargetClassId(value);
                  }}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    fontSize: 13,
                    cursor: 'pointer',
                    minWidth: 180,
                  }}
                >
                  <option value="">Move to class…</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} (Level {cls.level})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={!selectedClassForStudents || selectedStudentIdsForProgress.length === 0 || !bulkProgressTargetClassId}
                  onClick={() => handleBulkProgress('move')}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background:
                      selectedClassForStudents &&
                      selectedStudentIdsForProgress.length > 0 &&
                      bulkProgressTargetClassId
                        ? '#6366f1'
                        : '#e5e7eb',
                    color:
                      selectedClassForStudents &&
                      selectedStudentIdsForProgress.length > 0 &&
                      bulkProgressTargetClassId
                        ? '#eef2ff'
                        : '#6b7280',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor:
                      selectedClassForStudents &&
                      selectedStudentIdsForProgress.length > 0 &&
                      bulkProgressTargetClassId
                        ? 'pointer'
                        : 'default',
                  }}
                >
                  Move Selected
                </button>
              </div>
            </div>

            <div style={{ maxHeight: 400, overflow: 'auto', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              {loadingClassStudents ? (
                <div style={{ padding: 40, textAlign: 'center', fontSize: 14, color: '#6b7280' }}>
                  Loading students…
                </div>
              ) : !selectedClassForStudents ? (
                <div style={{ padding: 40, textAlign: 'center', fontSize: 14, color: '#6b7280' }}>
                  Select a class above to view its students.
                </div>
              ) : classStudents.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', fontSize: 14, color: '#6b7280' }}>
                  No students found for this class.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={
                            classStudents.length > 0 &&
                            selectedStudentIdsForProgress.length === classStudents.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              const allIds = classStudents.map(s => s.id);
                              setSelectedStudentIdsForProgress(allIds);
                            } else {
                              setSelectedStudentIdsForProgress([]);
                            }
                          }}
                        />
                      </th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12 }}>Student ID</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12 }}>Name</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12 }}>Gender</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12 }}>{classLevelWord(currentSchool)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classStudents
                      .filter((s) => {
                        if (!classStudentSearch.trim()) return true;
                        const term = classStudentSearch.toLowerCase();
                        const idDisplay = (s.student_id || s.login_id || '').toLowerCase();
                        const name = (s.name || '').toLowerCase();
                        return idDisplay.includes(term) || name.includes(term);
                      })
                      .map((student) => {
                        const checked = selectedStudentIdsForProgress.includes(student.id);
                        return (
                          <tr
                            key={student.id}
                            style={{
                              borderBottom: '1px solid #f3f4f6',
                              background: checked ? '#eff6ff' : '#fff',
                            }}
                          >
                            <td style={{ padding: '8px 16px' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleStudentSelectionForProgress(student.id)}
                              />
                            </td>
                            <td style={{ padding: '8px 16px', fontSize: 13, color: '#111827' }}>
                              {student.student_id || student.login_id || '—'}
                            </td>
                            <td style={{ padding: '8px 16px', fontSize: 13, color: '#111827' }}>
                              {student.name}
                            </td>
                            <td style={{ padding: '8px 16px', fontSize: 13, color: '#6b7280' }}>
                              {student.gender || '—'}
                            </td>
                            <td style={{ padding: '8px 16px', fontSize: 13, color: '#6b7280' }}>
                              {student.form ?? '—'}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  };

  const renderClubManagement = () => {
    return (
      <div style={{ padding: 40, flex: 1 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              color: '#1f2937',
              marginBottom: 8
            }}>
              🏆 Club Management
            </h2>
            <div className={styles.learnMoreTrigger}>
              <button
                type="button"
                aria-label="Learn more about Club Management"
                className={styles.learnMoreButton}
              >
                ?
              </button>
              <div className={styles.learnMoreTooltip}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use this page</div>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                  <li>Create clubs for extracurricular activities and organizations.</li>
                  <li>Use Manage Members to add teachers and students, then set roles.</li>
                  <li>Inactive clubs are hidden from student announcement filters.</li>
                  <li>Deleting a club also removes its announcements.</li>
                </ul>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 15, color: '#6b7280' }}>
              Manage clubs and their members
            </p>
          </div>
          
          {!showClubForm && (
            <button
              onClick={() => {
                setShowClubForm(true);
                setEditingClubId(null);
                setClubFormData({
                  name: '',
                  description: '',
                  status: 'active',
                });
              }}
              style={{
                padding: '12px 24px',
                background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <span style={{ fontSize: 18 }}>+</span>
              Add New Club
            </button>
          )}
        </div>

        {/* Club Form Modal */}
        {showClubForm && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} style={{ maxWidth: 900 }}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{editingClubId ? 'Edit Club' : 'Create New Club'}</h3>
                <button
                  type="button"
                  className={styles.modalClose}
                  onClick={() => {
                    setShowClubForm(false);
                    setEditingClubId(null);
                    setClubFormData({
                      name: '',
                      description: '',
                      status: 'active',
                    });
                  }}
                >
                  ×
                </button>
              </div>
              <div className={styles.modalBody}>
                <div style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: 8
                }}>
            <h3 style={{
              margin: '0 0 24px 0',
              fontSize: 20,
              fontWeight: 600,
              color: '#1f2937'
            }}>
              {editingClubId ? 'Edit Club' : 'Create New Club'}
            </h3>

            <form onSubmit={handleClubFormSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 24, marginBottom: 24 }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Club Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={clubFormData.name}
                    onChange={(e) => setClubFormData({ ...clubFormData, name: e.target.value })}
                    placeholder="e.g., Debate Club, Chess Club"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 8
                  }}>
                    Status *
                  </label>
                  <select
                    value={clubFormData.status}
                    onChange={(e) => setClubFormData({ ...clubFormData, status: e.target.value as 'active' | 'inactive' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 10,
                      fontSize: 15,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 8
                }}>
                  Description
                </label>
                <textarea
                  value={clubFormData.description}
                  onChange={(e) => setClubFormData({ ...clubFormData, description: e.target.value })}
                  placeholder="Optional description for this club"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 15,
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="submit"
                  style={{
                    padding: '12px 32px',
                    background: currentSchool === 'TLC' ? '#15803d' : '#1e3a5f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {editingClubId ? 'Update Club' : 'Create Club'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowClubForm(false);
                    setEditingClubId(null);
                    setClubFormData({
                      name: '',
                      description: '',
                      status: 'active',
                    });
                  }}
                  style={{
                    padding: '12px 32px',
                    background: '#fff',
                    color: '#374151',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Clubs List */}
        {loadingClubs ? (
          <div style={{
            padding: 60,
            textAlign: 'center',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: 18, color: '#6b7280' }}>Loading clubs...</div>
          </div>
        ) : clubs.length === 0 ? (
          <div style={{
            padding: 60,
            textAlign: 'center',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏆</div>
            <div style={{ fontSize: 18, color: '#374151', fontWeight: 600, marginBottom: 8 }}>
              No Clubs Yet
            </div>
            <div style={{ fontSize: 15, color: '#6b7280' }}>
              Click "Add New Club" to create your first club
            </div>
          </div>
        ) : (
          <div style={{
            background: '#fff',
            borderRadius: 16,
            overflowX: 'auto',
            overflowY: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Name
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Description
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status
                  </th>
                  <th style={{ padding: '16px 24px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {clubs.map((club) => (
                  <tr key={club.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '16px 24px', fontSize: 15, color: '#1f2937', fontWeight: 600 }}>
                      {club.name}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: 14, color: '#6b7280' }}>
                      {club.description || '-'}
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        background: club.status === 'active' ? '#d1fae5' : '#fee2e2',
                        color: club.status === 'active' ? '#065f46' : '#991b1b'
                      }}>
                        {club.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => openClubMembers(club)}
                          style={{
                            padding: '8px 16px',
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Manage Members
                        </button>
                        <button
                          onClick={() => handleEditClub(club)}
                          style={{
                            padding: '8px 16px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClub(club.id, club.name)}
                          style={{
                            padding: '8px 16px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Club Members Drawer */}
        {showClubMembers && selectedClubForMembers && (
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: `${clubDrawerWidth}px`,
            maxWidth: '100%',
            background: '#ffffff',
            boxShadow: '-4px 0 16px rgba(15,23,42,0.25)',
            zIndex: 40,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div
              onMouseDown={() => setIsResizingClubDrawer(true)}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: 'ew-resize',
                background: 'transparent',
              }}
              aria-label="Resize club members panel"
            />
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                  Manage Members – {selectedClubForMembers.name}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  Add teachers and students, update roles, or remove members.
                </div>
              </div>
              <button
                onClick={() => {
                  setShowClubMembers(false);
                  setSelectedClubForMembers(null);
                  setClubMembers([]);
                  setSelectedTeacherIdForClub('');
                  setSelectedStudentIdForClub('');
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 20,
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
                aria-label="Close members panel"
              >
                ×
              </button>
            </div>

            <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Add Teacher Manager
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <select
                  value={selectedTeacherIdForClub}
                  onChange={(e) => setSelectedTeacherIdForClub(e.target.value ? Number(e.target.value) : '')}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    fontSize: 14,
                  }}
                >
                  <option value="">Select teacher...</option>
                  {teacherAccounts.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name} ({teacher.email})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddTeacherToClub}
                  style={{
                    padding: '8px 14px',
                    background: '#10b981',
                    color: '#ffffff',
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Add
                </button>
              </div>

              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                Add Student
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={selectedStudentIdForClub}
                  onChange={(e) => setSelectedStudentIdForClub(e.target.value ? Number(e.target.value) : '')}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    fontSize: 14,
                  }}
                >
                  <option value="">Select student...</option>
                  {users
                    .filter((u) => u.role === 'student')
                    .map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name}
                        {student.student_id ? ` (${student.student_id})` : ''}
                      </option>
                    ))}
                </select>
                <button
                  onClick={handleAddStudentToClub}
                  style={{
                    padding: '8px 14px',
                    background: '#3b82f6',
                    color: '#ffffff',
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Add
                </button>
              </div>
            </div>

            <div style={{ padding: 20, overflowY: 'auto', overflowX: 'auto', flex: 1 }}>
              {loadingClubMembers ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  Loading members...
                </div>
              ) : clubMembers.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  No members in this club yet.
                </div>
              ) : (
                <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '8px 4px' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px' }}>Role</th>
                      <th style={{ textAlign: 'left', padding: '8px 4px' }}>{classLevelWord(currentSchool)}</th>
                      <th style={{ textAlign: 'right', padding: '8px 4px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubMembers.map((member) => (
                      <tr key={member.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 4px', minWidth: 0 }}>
                          <div
                            title={member.name}
                            style={{
                              fontWeight: 600,
                              color: '#111827',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 220,
                            }}
                          >
                            {member.name}
                          </div>
                          <div
                            title={member.email}
                            style={{
                              color: '#6b7280',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 220,
                            }}
                          >
                            {member.email}
                          </div>
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <select
                            value={member.pivot?.role || 'member'}
                            onChange={(e) => handleUpdateClubMemberRole(member.id, e.target.value)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 6,
                              border: '1px solid #d1d5db',
                              fontSize: 12,
                            }}
                          >
                            <option value="teacher">Teacher</option>
                            <option value="student">Student</option>
                            <option value="president">President</option>
                            <option value="vice_president">Vice President</option>
                            <option value="secretary">Secretary</option>
                            <option value="member">Member</option>
                          </select>
                        </td>
                        <td style={{ padding: '6px 4px', color: '#4b5563' }}>
                          {member.form ? formatClassLevelLabel(currentSchool, member.form) : '—'}
                        </td>
                        <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleRemoveClubMember(member.id, member.name)}
                            style={{
                              padding: '4px 10px',
                              background: '#fee2e2',
                              color: '#b91c1c',
                              border: 'none',
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

      </div>
    );
  };

  const renderProfile = () => (
    <div style={{ padding: 40, flex: 1 }}>
      <div className={styles.sectionHeader}>
        <h1 className={styles.sectionTitle}>👤 My Profile</h1>
        <p className={styles.sectionSubtitle}>Manage your personal information and preferences</p>
      </div>

      {/* Profile Picture Section */}
      <div className={styles.avatarSection}>
        <div className={styles.avatarWrapper} onClick={handleAvatarClick}>
          {avatarPreview ? (
            <img src={avatarPreview} alt="Profile" className={styles.avatarPreview} />
          ) : (
            <div className={styles.avatarPlaceholder}>
              {adminName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className={styles.avatarOverlay}>
            <span style={{ color: '#fff', fontSize: 24 }}>📷</span>
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleAvatarChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
        <div className={styles.avatarActions}>
          <div className={styles.avatarInfo}>
            <strong>{adminName}</strong>
            <br />
            Click on the profile picture to change it
          </div>
          {pendingAvatarFile && (
            <button
              type="button"
              onClick={handleAvatarSave}
              className={styles.primaryButton}
              style={{ padding: '10px 20px', fontSize: 13 }}
            >
              💾 Save Profile Picture
            </button>
          )}
          {avatarPreview && !pendingAvatarFile && (
            <button
              type="button"
              onClick={handleRemoveAvatar}
              className={styles.dangerButton}
              style={{ padding: '10px 20px', fontSize: 13 }}
            >
              🗑️ Remove Picture
            </button>
          )}
        </div>
        {avatarError && (
          <div style={{ fontSize: 13, color: '#ef4444', marginTop: 12 }}>{avatarError}</div>
        )}
      </div>

      {/* Profile Information Fields */}
      <div className={styles.contentCard}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>📋</span>
            Personal Information
          </div>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Full Name</label>
            <div className={styles.formInput} style={{ background: '#f8fafc', cursor: 'not-allowed' }}>
              {adminName}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Role</label>
            <div className={styles.formInput} style={{ background: '#f8fafc', cursor: 'not-allowed' }}>
              Administrator
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter your phone number"
              className={styles.formInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Email Address</label>
            <input
              type="email"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              placeholder="your.email@example.com"
              className={styles.formInput}
            />
            <div className={styles.formHint}>
              Used for announcements and password recovery
            </div>
          </div>

          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className={styles.primaryButton}
          >
            {savingProfile ? '⏳ Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );

  const studentUsersForBoarding = useMemo(() => {
    const students = users.filter(u => u.role === 'student');
    const q = boardingStudentSearch.trim().toLowerCase();
    const searched = !q
      ? students
      : students.filter(u =>
          u.name.toLowerCase().includes(q) ||
          (u.login_id && u.login_id.toLowerCase().includes(q))
        );
    const byForm = boardingFilterForm === 'all' ? searched : searched.filter(u => u.form === boardingFilterForm);

    const byStatus = byForm.filter((u) => {
      const status = boardingStatusMap[u.id]?.boarding_status;
      if (boardingFilterStatus === 'all') return true;
      if (boardingFilterStatus === 'unset') return !status;
      return status === boardingFilterStatus;
    });

    return byStatus.filter((u) => {
      const source = boardingStatusMap[u.id]?.admin_override;
      if (boardingFilterSource === 'all') return true;
      if (boardingFilterSource === 'unset') return !boardingStatusMap[u.id];
      if (boardingFilterSource === 'admin') return source === true;
      return source === false;
    });
  }, [users, boardingStudentSearch, boardingFilterForm, boardingFilterStatus, boardingFilterSource, boardingStatusMap]);

  const selectedBoardingSet = useMemo(
    () => new Set(selectedBoardingStudentIds),
    [selectedBoardingStudentIds]
  );

  const allVisibleSelected = studentUsersForBoarding.length > 0 &&
    studentUsersForBoarding.every((u) => selectedBoardingSet.has(u.id));

  const renderSystemSettings = (mode: 'all' | 'term' | 'boarding' = 'all') => {
    if (loadingSettings) {
      return (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Loading settings...</div>
      );
    }

    return (
      <div>
        <div className={styles.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 className={styles.sectionTitle}>
              {mode === 'term' ? 'Term Configuration' : mode === 'boarding' ? 'Boarding Controls' : 'System Settings'}
            </h2>
            {renderLearnMore('Learn more about System Settings', [
              'Set the current academic year and term for your school.',
              'Configure term dates so the system knows the active period.',
              'Lock boarding status to prevent students from changing it.',
              'Use Advance Term at the end of a term to start fresh.',
              'Always export term data before advancing to keep records.',
            ])}
          </div>
          <p className={styles.sectionSubtitle}>
            {mode === 'term'
              ? `Manage term configuration and transitions for ${currentSchool}`
              : mode === 'boarding'
              ? `Manage boarding lock and per-student boarding controls for ${currentSchool}`
              : `Manage term configuration, boarding controls, and term transitions for ${currentSchool}`}
          </p>
        </div>

        {settingsStatus && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 14px',
              borderRadius: 10,
              border: settingsStatus.type === 'success' ? '1px solid #bbf7d0' : '1px solid #fecaca',
              background: settingsStatus.type === 'success' ? '#f0fdf4' : '#fef2f2',
              color: settingsStatus.type === 'success' ? '#166534' : '#991b1b',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {settingsStatus.message}
          </div>
        )}

        {/* Term Configuration Card */}
        {mode !== 'boarding' && (
        <div className={styles.card} style={{ marginBottom: 24 }}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              <span className={styles.cardTitleIcon}>📅</span>
              Term Configuration
            </div>
          </div>
          <div className={styles.cardBody}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Academic Year</label>
                <input
                  type="text"
                  value={settingsForm.current_academic_year}
                  onChange={(e) => setSettingsForm(f => ({ ...f, current_academic_year: e.target.value }))}
                  placeholder="e.g. 2026"
                  className={styles.formInput}
                  maxLength={10}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Current Term</label>
                <select
                  value={settingsForm.current_term}
                  onChange={(e) => setSettingsForm(f => ({ ...f, current_term: Number(e.target.value) }))}
                  className={styles.formInput}
                >
                  <option value={1}>Term 1</option>
                  <option value={2}>Term 2</option>
                  <option value={3}>Term 3</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Term Start Date</label>
                <input
                  type="date"
                  value={settingsForm.term_start_date}
                  onChange={(e) => setSettingsForm(f => ({ ...f, term_start_date: e.target.value }))}
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Term End Date</label>
                <input
                  type="date"
                  value={settingsForm.term_end_date}
                  onChange={(e) => setSettingsForm(f => ({ ...f, term_end_date: e.target.value }))}
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Next Term Opening</label>
                <input
                  type="date"
                  value={settingsForm.next_term_opening}
                  onChange={(e) => setSettingsForm(f => ({ ...f, next_term_opening: e.target.value }))}
                  className={styles.formInput}
                />
              </div>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleSaveTermConfiguration}
                disabled={savingSettings}
                className={styles.primaryButton}
              >
                {savingSettings ? 'Saving...' : 'Save Term Configuration'}
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Boarding Lock Card */}
        {mode !== 'term' && (
        <div className={styles.card} style={{ marginBottom: 24 }}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              <span className={styles.cardTitleIcon}>🔒</span>
              Boarding Status Controls
            </div>
          </div>
          <div className={styles.cardBody}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: 16, background: settingsForm.boarding_locked ? '#fef2f2' : '#f0fdf4', borderRadius: 10, border: `1px solid ${settingsForm.boarding_locked ? '#fecaca' : '#bbf7d0'}` }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={boardingLockDraft}
                  onChange={(e) => setBoardingLockDraft(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                Lock Student Boarding Selection
              </label>
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                {boardingLockDraft
                  ? 'Students cannot change their boarding status.'
                  : 'Students can choose their own boarding status.'}
              </span>
            </div>

            <div style={{ marginTop: -4, marginBottom: 12 }}>
              <button
                type="button"
                onClick={handleApplyBoardingLock}
                disabled={savingBoardingLock}
                className={styles.primaryButton}
              >
                {savingBoardingLock ? 'Applying...' : 'Apply Boarding Lock'}
              </button>
            </div>

            <div style={{ padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Per-Student Boarding Controls</div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <input
                  type="text"
                  placeholder="Search students by name or ID..."
                  value={boardingStudentSearch}
                  onChange={(e) => setBoardingStudentSearch(e.target.value)}
                  className={styles.formInput}
                  style={{ minWidth: 220, flex: 1 }}
                />
                <select
                  value={boardingFilterForm}
                  onChange={(e) => setBoardingFilterForm(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  className={styles.formInput}
                  style={{ width: 140 }}
                >
                  <option value="all">All forms</option>
                  {Array.from({ length: classLevelMax }, (_, i) => i + 1).map((f) => (
                    <option key={f} value={f}>
                      {formatClassLevelLabel(currentSchool, f)}
                    </option>
                  ))}
                </select>
                <select
                  value={boardingFilterStatus}
                  onChange={(e) =>
                    setBoardingFilterStatus(e.target.value as 'all' | 'boarder' | 'day_scholar' | 'unset')
                  }
                  className={styles.formInput}
                  style={{ width: 180 }}
                >
                  <option value="all">All statuses</option>
                  <option value="boarder">Boarder</option>
                  <option value="day_scholar">Day Scholar</option>
                  <option value="unset">Unset</option>
                </select>
                <select
                  value={boardingFilterSource}
                  onChange={(e) => setBoardingFilterSource(e.target.value as 'all' | 'admin' | 'student' | 'unset')}
                  className={styles.formInput}
                  style={{ width: 180 }}
                >
                  <option value="all">All sources</option>
                  <option value="admin">Admin override</option>
                  <option value="student">Student set</option>
                  <option value="unset">Unset</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setSelectedBoardingStudentIds(studentUsersForBoarding.map((u) => u.id))}
                >
                  {allVisibleSelected ? 'Selected All Visible' : 'Select All Visible'}
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setSelectedBoardingStudentIds([])}
                >
                  Clear Selection
                </button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>Selected: {selectedBoardingStudentIds.length}</span>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => handleSetSelectedBoarding('day_scholar')}
                  disabled={applyingBulkBoarding || selectedBoardingStudentIds.length === 0}
                >
                  Set to Day Scholar
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => handleSetSelectedBoarding('boarder')}
                  disabled={applyingBulkBoarding || selectedBoardingStudentIds.length === 0}
                >
                  Set to Boarder
                </button>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={handleClearSelectedBoardingStatuses}
                  disabled={applyingBulkBoarding || selectedBoardingStudentIds.length === 0}
                >
                  {applyingBulkBoarding ? 'Applying...' : 'Clear Statuses'}
                </button>
              </div>

              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', width: 40, textAlign: 'center' }}>✓</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Student</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>ID</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>{classLevelWord(currentSchool)}</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Current Status</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Source</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentUsersForBoarding.slice(0, 150).map((student) => {
                      const status = boardingStatusMap[student.id];
                      const isSelected = selectedBoardingSet.has(student.id);
                      return (
                        <tr key={student.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                setSelectedBoardingStudentIds((prev) =>
                                  e.target.checked ? [...new Set([...prev, student.id])] : prev.filter((id) => id !== student.id)
                                );
                              }}
                            />
                          </td>
                          <td style={{ padding: '8px 12px' }}>{student.name}</td>
                          <td style={{ padding: '8px 12px', color: '#6b7280' }}>{student.login_id}</td>
                          <td style={{ padding: '8px 12px' }}>{student.form ?? '-'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            {status ? (status.boarding_status === 'boarder' ? 'Boarder' : 'Day Scholar') : 'Not Set'}
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {status ? (status.admin_override ? 'Admin override' : 'Student set') : 'Unset'}
                          </td>
                          <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                            {status?.updated_at ? new Date(status.updated_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    {studentUsersForBoarding.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 16, textAlign: 'center', color: '#9ca3af' }}>
                          No students found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Term Transition Card */}
        {mode !== 'boarding' && (
        <div className={styles.card} style={{ marginBottom: 24 }}>
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>
              <span className={styles.cardTitleIcon}>🔄</span>
              Term Transition
            </div>
          </div>
          <div className={styles.cardBody}>
            <p style={{ color: '#6b7280', marginBottom: 16, lineHeight: 1.6 }}>
              Advancing the term opens the next term’s fee accounts with one brought-forward balance.
              Mid-year advances keep every student in the same class. Moving from Term 3 to Term 1
              promotes active students and graduates the final class. Published grades stay on the old term.
              Always download the term export first.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleExportTermData}
                disabled={exportingTermData}
                className={styles.primaryButton}
                style={{ background: '#059669' }}
              >
                {exportingTermData ? 'Exporting...' : `Export Term ${schoolSettings?.current_term ?? ''} Data`}
              </button>
              <button
                type="button"
                onClick={() => setShowAdvanceTermModal(true)}
                className={styles.primaryButton}
                style={{ background: '#dc2626' }}
              >
                Advance to Next Term
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Advance Term Confirmation Modal */}
        {showAdvanceTermModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: '#dc2626' }}>Advance Term</h3>
              <p style={{ color: '#4b5563', lineHeight: 1.6, marginBottom: 16 }}>
                You are about to advance <strong>{currentSchool}</strong> from
                <strong> Term {schoolSettings?.current_term} {schoolSettings?.current_academic_year}</strong> to
                <strong> Term {(schoolSettings?.current_term ?? 0) >= 3 ? 1 : (schoolSettings?.current_term ?? 0) + 1} {(schoolSettings?.current_term ?? 0) >= 3 ? String(Number(schoolSettings?.current_academic_year ?? 0) + 1) : schoolSettings?.current_academic_year}</strong>.
              </p>
              <p style={{ color: '#ef4444', fontWeight: 600, marginBottom: 16 }}>
                Unpublished grades and assignments for the current term will be cleared.
                {(schoolSettings?.current_term ?? 0) >= 3
                  ? ' Final-class students will be graduated, and other active students will move up one class.'
                  : ' Classes stay the same until the year ends.'}
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={advanceTermConfirmed}
                  onChange={(e) => setAdvanceTermConfirmed(e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 600 }}>I have exported term data and confirm this action</span>
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowAdvanceTermModal(false); setAdvanceTermConfirmed(false); }}
                  className={styles.secondaryButton}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAdvanceTerm}
                  disabled={!advanceTermConfirmed || advancingTerm}
                  className={styles.primaryButton}
                  style={{ background: '#dc2626', opacity: (!advanceTermConfirmed || advancingTerm) ? 0.5 : 1 }}
                >
                  {advancingTerm ? 'Advancing...' : 'Advance Term'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSection = () => {
    switch (section) {
      case 'dashboard':
        return renderDashboard();
      case 'user-management':
        return renderUserManagement();
      case 'password-management':
        return renderPasswordManagement();
      case 'grade-review':
        return renderGradeReview();
      case 'grades':
        return renderGrades();
      case 'announcements':
        return renderAnnouncements();
      case 'timetable-management':
        return (
          <TimetableGrid
            timetables={timetables}
            conflictMessages={conflictMessages}
            loading={loadingTimetables}
            viewMode={timetableViewMode}
            school={currentSchool}
            activeForm={timetableForm}
            selectedTeacherId={selectedTeacherId}
            classes={classes}
            subjects={subjects}
            linkSubjects={timetableLinkSubjects}
            linkedTeacherBySubjectId={timetableLinkedTeacherBySubjectId}
            teacherAccounts={teacherAccounts}
            publishing={publishingTimetable}
            generating={generatingTimetable}
            clearing={clearingTimetable}
            onViewModeChange={(mode) => {
              setTimetableViewMode(mode);
              if (mode === 'teacher') {
                if (teacherAccounts.length > 0 && !selectedTeacherId) {
                  setSelectedTeacherId(teacherAccounts[0].id);
                }
              } else {
                setSelectedTeacherId(null);
              }
              setConflictMessages({});
            }}
            onFormChange={(form) => {
              setTimetableForm(form);
              setConflictMessages({});
            }}
            onTeacherChange={(id) => {
              setSelectedTeacherId(id);
              setConflictMessages({});
            }}
            onPublish={handlePublishTimetable}
            onGenerate={handleGenerateTimetable}
            onClear={handleClearTimetable}
            onCreateEntry={async (payload) => {
              await createTimetable({ ...payload, school: currentSchool });
              await loadTimetables();
            }}
            onUpdateEntry={async (id, updates) => {
              const result = await updateTimetable(id, updates);
              await loadTimetables();
              if (result.conflicts && result.conflicts.length > 0) {
                setConflictMessages(prev => ({ ...prev, [id]: result.conflicts! }));
              } else {
                setConflictMessages(prev => {
                  const next = { ...prev };
                  delete next[id];
                  return next;
                });
              }
            }}
            onDeleteEntry={async (id) => {
              await deleteTimetable(id);
              await loadTimetables();
            }}
          />
        );
      case 'class-teacher-linking':
        return renderClassTeacherLinking();
      case 'class-management':
        return renderClassManagement();
      case 'club-management':
        return renderClubManagement();
      case 'fee-structure':
        return <FeeManagementGate school={currentSchool} section="structure" />;
      case 'fee-history':
        return <FeeManagementGate school={currentSchool} section="history" />;
      case 'fee-report':
        return <FeeManagementGate school={currentSchool} section="report" />;
      case 'report-cards':
        return <ReportCardManagement school={currentSchool} />;
      case 'audit-log':
        return renderAuditLog();
      case 'term-configuration':
        return renderSystemSettings('term');
      case 'boarding-controls':
        return renderSystemSettings('boarding');
      case 'system-settings':
        return renderSystemSettings();
      case 'profile':
        return renderProfile();
      default:
        return null;
    }
  };

  const getSectionLabel = (sectionId: AdminSection): string => {
    if (sectionId === 'dashboard') return 'Home';
    if (sectionId === 'profile') return 'My Profile';
    for (const g of NAV_GROUPS) {
      const match = g.items.find(i => i.id === sectionId);
      if (match) return match.label;
    }
    return 'Dashboard';
  };

  const handleSectionChange = async (nextSection: AdminSection) => {
    if (nextSection === section) return;
    const startedAt = Date.now();
    setIsSectionTransitioning(true);
    try {
      setSection(nextSection);
      await waitMinDuration(startedAt, 250);
    } finally {
      setIsSectionTransitioning(false);
    }
  };

  useEffect(() => {
    if (section !== 'announcements') return;
    markAnnouncementsAsSeen();
  }, [section, announcements]);

  const isHub = section === 'dashboard';
  const activeGroup = NAV_GROUPS.find(g => g.items.some(i => i.id === section));

  return (
    <div className={`${styles.dashboardContainer} ${isHub ? styles.hubLayout : ''}`} data-school={currentSchool === 'TLC' ? 'tlc' : 'kba'}>
      {/* Mobile Menu Toggle — hidden on hub since there's no sidebar */}
      {isMobileView && !isHub && (
        <button 
          className={styles.mobileMenuToggle}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          ☰
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && !isHub && (
        <div 
          className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.show : ''}`}
          onClick={closeMobileMenu}
          aria-label="Close menu"
        />
      )}

      {(isSwitchingSchool || isSectionTransitioning) && (
        <LoadingOverlay label={isSwitchingSchool ? 'Switching school...' : 'Loading section...'} />
      )}

      {/* Sidebar — only rendered inside subsystems, not on the hub */}
      {!isHub && (
        <aside className={`${styles.sidebar} ${isMobileMenuOpen ? styles.mobileOpen : ''}`}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarLogo}>
              <div className={styles.logoIcon}>
                <img 
                  src={currentSchool === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg'} 
                  alt={`${currentSchool} Logo`} 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                />
              </div>
              <div className={styles.logoText}>
                <span className={styles.sidebarTitle}>{schoolInfo.shortName}</span>
                <span className={styles.sidebarSubtitle}>{activeGroup?.label ?? 'Admin Portal'}</span>
              </div>
            </div>
          </div>

          <nav className={styles.navSection}>
            {/* Back to hub */}
            <button
              className={`${styles.navButton} ${styles.backToHub}`}
              onClick={() => { void handleSectionChange('dashboard'); closeMobileMenu(); }}
            >
              <span className={styles.navIcon}>←</span>
              Back to Hub
            </button>

            {activeGroup && (
              <>
                <div className={styles.navLabel}>{activeGroup.label}</div>
                {activeGroup.items.map(({ id, label, icon }) => (
                  <button
                    key={id}
                    className={`${styles.navButton} ${section === id ? styles.active : ''}`}
                    onClick={() => { void handleSectionChange(id); closeMobileMenu(); }}
                  >
                    <span className={styles.navIcon}>{icon}</span>
                    {label}
                  </button>
                ))}
              </>
            )}
          </nav>

          <div className={styles.sidebarProfile}>
            <button
              className={styles.profileButton}
              onClick={() => {
                void handleSectionChange('profile');
                closeMobileMenu();
              }}
            >
              <div className={styles.profileAvatar}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Profile" />
                ) : (
                  adminName?.charAt(0)?.toUpperCase() || 'A'
                )}
              </div>
              <div className={styles.profileInfo}>
                <div className={styles.profileName}>{adminName}</div>
                <div className={styles.profileRole}>{user?.demo_mode ? 'Demo' : 'Administrator'}</div>
              </div>
            </button>
            <button 
              className={styles.logoutButton} 
              onClick={() => {
                closeMobileMenu();
                setShowLogoutConfirmation(true);
              }}
            >
              🚪 Sign Out
            </button>
            <div className={styles.sidebarAttribution}>POWERED BY BIZNARY TECH SOLUTIONS</div>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <div className={styles.mainContent}>
        {user?.demo_mode && (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <span>Demo dataset. Live students, fees, and settings are unchanged.</span>
            <button type="button" onClick={() => navigate('/dev')} style={{ background: '#92400e', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', fontWeight: 600, cursor: 'pointer' }}>
              Demo controls
            </button>
          </div>
        )}
        {/* Top Bar */}
        <header className={styles.topBar}>
          <div className={styles.topBarLeft}>
            <div className={styles.breadcrumb}>
              Admin / <span>{getSectionLabel(section)}</span>
            </div>
          </div>
          <div className={styles.topBarRight}>
            <SchoolSwitcher />
            {schoolSettings && (
              <div
                title="Current system term"
                style={{
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: '1px solid #e5e7eb',
                  background: '#ffffff',
                  color: '#111827',
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Term {schoolSettings.current_term} • {schoolSettings.current_academic_year}
              </div>
            )}
            {section === 'dashboard' && (
              <button
                className={styles.topBarButton}
                onClick={() => setShowLogoutConfirmation(true)}
                title="Sign Out"
              >
                🚪
              </button>
            )}
            <button 
              className={styles.topBarButton}
              onClick={() => void handleSectionChange('announcements')}
              title="Announcements"
            >
              📢
              {newAnnouncementCount > 0 && (
                <span className={styles.topBarNotificationBadge}>{bellBadgeCount}</span>
              )}
            </button>
          </div>
        </header>

        {/* Subsystem Switcher Bar */}
        {section !== 'dashboard' && (
          <div className={styles.subsystemBar}>
            {NAV_GROUPS.map(group => {
              const isActive = getActiveSubsystem(section) === group.id;
              return (
                <button
                  key={group.id}
                  type="button"
                  className={`${styles.subsystemPill} ${isActive ? styles.subsystemPillActive : ''}`}
                  style={isActive ? { borderColor: group.color, color: group.color } : {}}
                  onClick={() => void handleSectionChange(group.items[0].id)}
                >
                  <span>{group.icon}</span>
                  {group.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Content Area */}
        <div className={styles.contentWrapper}>
          <div className={styles.contentInner}>
            {renderSection()}
          </div>
        </div>
      </div>

      {showStudentImport && (
        <StudentImportModal
          school={currentSchool}
          onClose={() => setShowStudentImport(false)}
          onImported={() => {
            void loadUsers();
          }}
        />
      )}

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutConfirmation}
        onConfirm={() => {
          setShowLogoutConfirmation(false);
          logout();
        }}
        onCancel={() => setShowLogoutConfirmation(false)}
      />
    </div>
  );
};
